import "./PipelinePage.css";
import { jsPDF } from "jspdf";
import { BasePageComponent } from "../../../shared/components/pages/BasePageComponent";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import {
  EventoResponse,
  OportunidadProductos,
  OportunidadResponse,
} from "../../../shared/response/ventas/OportunidadResponse";
import { PipelineStatsResponse } from "../../../shared/response/ventas/PipelineStatsResponse";
import { AgendaResponse } from "../../../shared/response/ventas/AgendaResponse";
import { ClienteSearchItem } from "../../../shared/response/ventas/ClienteSearchItem";
import { ClienteResponse } from "../../../shared/response/terceros/ClienteResponse";
import { FincaListItemResponse } from "../../../shared/response/terceros/FincaListItemResponse";
import { PromotorResponse } from "../../../shared/response/terceros/PromotorResponse";
import { PipelineService } from "../../services/ventas/PipelineService";
import { ClienteService, buscarClientes } from "../../services/terceros/ClienteService";
import { coincideBusqueda, palabrasBusqueda } from "../../../shared/utils/busquedaTexto";
import { ClienteSaveService } from "../../services/terceros/ClienteSaveService";
import { FincaSaveService } from "../../services/terceros/FincaSaveService";
import { FincaListByClienteIdService } from "../../../shared/services/terceros/FincaListByClienteIdService";
import { FincaPayload } from "../../../shared/services/api/payload/terceros/FincaPayload";
import { DepartamentoListService } from "../../../shared/services/comun/DepartamentoListService";
import { MunicipioListService } from "../../../shared/services/comun/MunicipioListService";
import { MunicipioResponse } from "../../../shared/response/comun/MunicipioResponse";
import { FincaDeleteService } from "../../services/terceros/FincaDeleteService";
import { PromotorService } from "../../services/terceros/PromotorService";
import { OportunidadSavePayload } from "../../../shared/services/api/payload/OportunidadSavePayload";
import { OportunidadStagePayload } from "../../../shared/services/api/payload/OportunidadStagePayload";
import { EventoPayload } from "../../../shared/services/api/payload/EventoPayload";
import { ExtenderDemoPayload } from "../../../shared/services/api/payload/ExtenderDemoPayload";
import { RenovarOportunidadPayload } from "../../../shared/services/api/payload/RenovarOportunidadPayload";
import { ClientePayload } from "../../../shared/services/api/payload/terceros/ClientePayload";
import { getServicios, getServicio } from "./Servicios";
import { ServicioItemDto } from "../../../shared/response/ventas/ServiciosResponse";
import { ProductosMarca } from "../../productosMarca";

// ── Constantes de dominio ──────────────────────────────────────────────────────

const STAGES = [
  { id: "new",         label: "Lead nuevo",       short: "Nuevo" },
  { id: "contacted",   label: "Contactado",        short: "Contactado" },
  { id: "demo",        label: "Demo agendada",     short: "Demo" },
  { id: "proposal",    label: "Propuesta enviada", short: "Propuesta" },
  { id: "closed_won",  label: "Ganado",            short: "Ganado" },
  { id: "closed_lost", label: "Perdido",           short: "Perdido" },
];

const CLOSED_STAGES = new Set(["closed_won", "closed_lost"]);

/** Colores de cada etapa: fondo, texto y borde de las tarjetas, y el color de la barra del embudo. El Dashboard usa
 *  el de la barra. */
export const SC: Record<string, { bg: string; text: string; border: string; bar: string }> = {
  new:         { bg: "var(--st-new-bg)",       text: "var(--st-new-text)",       border: "var(--st-new-border)",       bar: "#D0CEC5" },
  contacted:   { bg: "var(--st-contacted-bg)", text: "var(--st-contacted-text)", border: "var(--st-contacted-border)", bar: "#7F77DD" },
  demo:        { bg: "var(--st-demo-bg)",      text: "var(--st-demo-text)",       border: "var(--st-demo-border)",      bar: "#378ADD" },
  proposal:    { bg: "var(--st-proposal-bg)",  text: "var(--st-proposal-text)",   border: "var(--st-proposal-border)",  bar: "#EF9F27" },
  closed_won:  { bg: "var(--st-won-bg)",       text: "var(--st-won-text)",        border: "var(--st-won-border)",       bar: "#63A22C" },
  closed_lost: { bg: "var(--st-lost-bg)",      text: "var(--st-lost-text)",       border: "var(--st-lost-border)",      bar: "#B0AEA5" },
};

/** Nombre de cada fuente de la oportunidad. También lo usa el Dashboard. */
export const SOURCE_LABELS:  Record<string, string> = { web: "WEB", whatsapp: "WHATSAPP", app: "APP", campo: "CAMPO", manual: "MANUAL" };
const SOURCE_CLASSES: Record<string, string> = { web: "src-web", whatsapp: "src-whatsapp", app: "src-app", campo: "src-campo", manual: "src-manual" };
// Stage inicial que asigna el backend según la fuente (solo para el hint del modal).
// Toda op nueva entra en "Lead nuevo" sin importar la fuente (la fuente es solo metadata).
const SOURCE_STAGE_HINTS: Record<string, string> = { web: "Lead nuevo", app: "Lead nuevo", campo: "Lead nuevo", whatsapp: "Lead nuevo", manual: "Lead nuevo" };

// Categorías PROTEC de respaldo (cuando el servidor no devuelve precios)
const FALLBACK_PROTEC: Record<string, { label: string; precio: number }> = {
  cat1: { label: "Cat 1 — Maíz / Caña",    precio: 85_000  },
  cat2: { label: "Cat 2 — Café",            precio: 150_000 },
  cat3: { label: "Cat 3 — Frutales",        precio: 150_000 },
  cat4: { label: "Cat 4 — Frutales 100lts", precio: 170_000 },
  cat5: { label: "Cat 5 — Aguacate Hass",   precio: 200_000 },
  cat6: { label: "Cat 6 — Potreros",        precio: 125_000 },
};

// Precios de respaldo de RASTRO (por tramo de cantidad) y de la Báscula, sin IVA, cuando el servidor no devuelve
// tarifas. Son los de Sextante: la falla de carga se avisa en pantalla (ver `_loadData`).
const TRAMOS_RASTRO_RESPALDO: readonly TramoRastro[] = [
  { desde: 1,  hasta: 10,   precio: 530_000 },
  { desde: 11, hasta: 20,   precio: 500_000 },
  { desde: 21, hasta: null, precio: 470_000 },
];
const PRECIO_BASCULA_RESPALDO = 750_000;

/** Nombre de la etapa (`new` → «Lead nuevo»); si no la conoce, devuelve el código tal cual. También lo usa el
 *  Dashboard. */
export function getStageLabel(id: string): string {
  return STAGES.find(s => s.id === id)?.label ?? id;
}

/** Construye el texto de un evento del timeline según su tipo + metadata. */
function renderEventoDesc(ev: EventoResponse): string {
  const m = (ev.metadata ?? {}) as Record<string, unknown>;
  switch (ev.tipo) {
    case "stage_change": return `Etapa: ${getStageLabel(String(m.de ?? ""))} → ${getStageLabel(String(m.a ?? ""))}`;
    case "note":         return String(m.texto ?? "");
    case "llamada":      return "Llamada registrada";
    case "visita":       return "Visita registrada";
    case "demo":         return "Demo realizada";
    case "cierre":       return "Cierre registrado";
    case "demo_asignada":  return "Demo asignada (30 días)";
    case "demo_extendida": return `Demo extendida${m.dias ? ` (+${m.dias} días)` : ""}`;
    case "renovacion":     return "Renovación registrada";
    default:             return "";
  }
}

// ── Tipos locales ──────────────────────────────────────────────────────────────

interface CotizFinca { id: number; fincaId?: string; nombre: string; tipo: string; municipio: string; ha: number; }
interface CotizProds { dataag: boolean; rastro: number; bascula: number; vista: boolean; protec: { activo: boolean; cat: string; ha: number }; }
interface VISTAResult { base: number; transporte: number; vuelo: number; conteo: number; total: number; }
interface PROTECResult { label: string; pxHa: number; ha: number; total: number; }
/** Tramo de precio de RASTRO: de `desde` a `hasta` unidades (`hasta` null = sin tope); precio por unidad sin IVA. */
interface TramoRastro { desde: number; hasta: number | null; precio: number; }

type ModalStep = "search" | "existing" | "new" | "edit";

// ── PipelinePage ───────────────────────────────────────────────────────────────

export class PipelinePage extends BasePageComponent {
  // Deep-link: otra página (ej. Campañas) puede pedir abrir una ficha al montar el pipeline.
  static pendingFicha: { clienteId: string; opId?: string } | null = null;

  /**
   * Id de promotor del usuario autenticado (`LoginPlataformaResponse.promotorId`).
   * Si viene, el pipeline se restringe a sus oportunidades. La FK de la oportunidad
   * hacia `tbl_promotores` es `comercialId`, así que ese es el filtro.
   * OJO: esto acota la vista, no es control de acceso — quien filtra de verdad es el backend.
   */
  constructor(
    /** Lo que vende la marca (2026-10-20): el resto no se ofrece, no se cotiza ni se suma (ver `ProductosMarca`). */
    private readonly _productos: ProductosMarca,
    private readonly _promotorId: string | null = null,
    /** Usuario con rol comercial pero sin ficha de promotor: no hay id por el que acotar la vista. */
    private readonly _comercialSinPromotor = false,
  ) {
    super();
  }

  // Estado del pipeline
  private _ops:       OportunidadResponse[] = [];
  private _clientes:  ClienteResponse[]     = [];
  private _promotores: PromotorResponse[] = [];

  // Ficha
  private _currentClienteId: string | null = null;
  private _currentOpId:      string | null = null;
  private _clienteOps:       OportunidadResponse[] = [];
  /**
   * Fincas reales del cliente de la ficha. No se usa `cliente.hectareasTotales`: ese campo
   * queda en 0 desde que se crea el cliente hasta el siguiente sync (la finca inicial se
   * guarda DESPUÉS del cliente), y con ha=0 los productos que se tarifan por hectárea
   * quedaban fuera del bloque de precios. Es la misma fuente que usa la cotización.
   */
  private _fichaFincas:      FincaListItemResponse[] = [];

  // Modal
  private _modalStep:       ModalStep = "search";
  private _modalCliente:    ClienteSearchItem | null = null;
  private _modalSource:     string = "web";
  private _geoMunis:        MunicipioResponse[] = [];
  private _modalPrefill:    string = "";   // lo tecleado en el buscador, para precargar NIT/nombre en cliente nuevo
  private _modalEditOp:     OportunidadResponse | null = null;   // != null → el modal está en modo edición


  // Agenda. El endpoint no acepta filtro de comercial, así que se trae el mes completo y se
  // filtra en cliente; la respuesta se cachea por mes para que cambiar de comercial no re-pegue al API.
  private _agendaMonth:       Date = new Date();
  private _agendaComercialId: string | null = null;   // null = toda la agenda (por defecto)
  private _agendaData:        AgendaResponse | null = null;
  private _agendaKey:         string = "";            // "año-mes" del `_agendaData` cacheado

  public getAppRoute(): string  { return "/ventas/pipeline"; }
  public getPageTitle(): string | null { return "Pipeline"; }

  // ── Init ────────────────────────────────────────────────────────────────────

  protected override renderPageContent(pageContent: HTMLDivElement): void {
    pageContent.className = "sx-page pl-page";
    pageContent.innerHTML = this._buildPageHTML();
    this._wirePageEvents();
    void this._loadData();
  }

  // ── HTML skeleton ────────────────────────────────────────────────────────────

  private _buildPageHTML(): string {
    return `
      <div class="pl-inner">

        ${this._comercialSinPromotor ? `
        <div class="sx-alert sx-alert-warning" style="margin-bottom:12px">
          <i class="ph ph-warning" aria-hidden="true"></i>
          <div>
            <div class="sx-alert-title">Estás viendo todo el pipeline</div>
            Tu cuenta tiene rol comercial pero no está vinculada a una ficha de promotor, así que no
            se puede acotar la vista a tus oportunidades. Pide a un administrador que te dé de alta
            en <strong>Terceros → Promotores</strong> con este mismo correo.
          </div>
        </div>` : ""}

        <div class="pl-pg-hd">
          <div>
            <div class="pl-pg-ti">Pipeline Comercial</div>
            <div class="pl-pg-su" id="pl-pg-su">· — oportunidades activas · — clientes registrados</div>
          </div>
          <button class="sx-btn sx-btn-primary sx-btn-sm" id="pl-nuevo-btn">
            <i class="ph ph-plus" aria-hidden="true"></i> Nueva oportunidad
          </button>
        </div>
        <div class="pl-topbar">
          <div class="pl-tabs">
            <button class="pl-tab active" data-view="kanban">Kanban</button>
            <button class="pl-tab" data-view="lista">Lista</button>
            <!-- Ficha es la única que sigue oculta: se revela en _openFicha al abrir una oportunidad. -->
            <button class="pl-tab" data-view="ficha" id="pl-tab-ficha" style="display:none">Ficha</button>
            <button class="pl-tab" data-view="agenda">Agenda</button>
            <button class="pl-tab" data-view="dashboard">Dashboard</button>
          </div>
        </div>

        <div class="pl-views">

          <!-- Kanban -->
          <div class="pl-view active" id="pl-view-kanban">
            <div class="pl-kanban-board" id="pl-kanban-board"></div>
          </div>

          <!-- Lista -->
          <div class="pl-view" id="pl-view-lista">
            <div class="pl-toolbar">
              <div class="pl-search-wrap">
                <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
                <input class="pl-search-input" id="pl-search" placeholder="Buscar cliente o NIT…">
              </div>
              <select class="pl-fsel" id="pl-filter-stage">
                <option value="">Todas las etapas</option>
                ${STAGES.map(s => `<option value="${s.id}">${s.label}</option>`).join("")}
              </select>
              <select class="pl-fsel" id="pl-filter-source">
                <option value="">Todas las fuentes</option>
                ${Object.entries(SOURCE_LABELS).map(([k, v]) => `<option value="${k}">${v}</option>`).join("")}
              </select>
              <span class="pl-count" id="pl-lista-count"></span>
            </div>
            <div class="pl-table-wrap">
              <table class="pl-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Fuente</th>
                    <th>Ha</th>
                    <th>Productos</th>
                    <th>Etapa</th>
                    <th>Valor DA</th>
                    <th>Próx. contacto</th>
                    <th>Comercial</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody id="pl-lista-tbody">
                  <tr><td colspan="9" class="pl-empty">Cargando…</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Ficha -->
          <div class="pl-view" id="pl-view-ficha">
            <div class="pl-ficha-msg" id="pl-ficha-msg">
              <i class="ph ph-cursor-click" style="font-size:32px;color:var(--sx-text-tertiary)"></i>
              <span>Selecciona una oportunidad en el Kanban o en la Lista</span>
            </div>
            <div id="pl-ficha-content"></div>
          </div>

          <!-- Agenda -->
          <div class="pl-view" id="pl-view-agenda">
            <div class="pl-agenda-header" id="pl-agenda-header">
              <button class="sx-btn sx-btn-outline sx-btn-sm" id="pl-agenda-prev">‹</button>
              <span id="pl-agenda-month-label"></span>
              <button class="sx-btn sx-btn-outline sx-btn-sm" id="pl-agenda-next">›</button>
              <select class="pl-fsel" id="pl-agenda-comercial"></select>
            </div>
            <div class="pl-agenda-grid" id="pl-agenda-grid">
              <div class="pl-empty" style="grid-column:1/-1">Cargando agenda…</div>
            </div>
          </div>

          <!-- Dashboard -->
          <div class="pl-view" id="pl-view-dashboard">
            <div class="pl-dash-inner" id="pl-dash-content">
              <div class="pl-empty">Cargando estadísticas…</div>
            </div>
          </div>

        </div>

        <!-- Modal nueva oportunidad (2 pasos) -->
        <div class="pl-modal-overlay" id="pl-modal-overlay">
          <div class="pl-modal" onclick="event.stopPropagation()">
            <div class="pl-modal-hd">
              <span class="pl-modal-title" id="pl-modal-title">Nueva oportunidad</span>
              <button class="pl-modal-close" id="pl-modal-close">✕</button>
            </div>
            <div id="pl-modal-content"></div>
          </div>
        </div>

      </div>
    `;
  }

  // ── Event wiring ─────────────────────────────────────────────────────────────

  private _wirePageEvents(): void {
    document.querySelectorAll<HTMLElement>(".pl-tab").forEach(tab => {
      tab.onclick = () => this._switchView(tab.dataset.view!);
    });

    document.getElementById("pl-nuevo-btn")!.onclick    = () => this._openModal();
    document.getElementById("pl-modal-close")!.onclick  = () => this._closeModal();
    document.getElementById("pl-modal-overlay")!.onclick = (e) => {
      if ((e.target as HTMLElement).id === "pl-modal-overlay") this._closeModal();
    };

    document.getElementById("pl-search")!.oninput        = () => this._renderLista();
    document.getElementById("pl-filter-stage")!.onchange  = () => this._renderLista();
    document.getElementById("pl-filter-source")!.onchange = () => this._renderLista();

    this._agendaMonth = new Date();
    document.getElementById("pl-agenda-prev")!.onclick = () => { this._agendaMonth.setMonth(this._agendaMonth.getMonth() - 1); void this._renderAgenda(); };
    document.getElementById("pl-agenda-next")!.onclick = () => { this._agendaMonth.setMonth(this._agendaMonth.getMonth() + 1); void this._renderAgenda(); };
  }

  // ── Data loading ──────────────────────────────────────────────────────────────

  /**
   * Carga las oportunidades, los clientes y los promotores, y una vez por sesión las tarifas del menú Costos y el
   * catálogo de servicios. Lo secundario que falla no corta la carga, pero se escribe en consola; si fallan las
   * tarifas también se avisa en pantalla, porque las cotizaciones pasan a los precios de respaldo del código.
   */
  private async _loadData(): Promise<void> {
    try {
      const pipeline = PipelineService.instance;
      const [opsRes, clientes, promotores] = await Promise.all([
        pipeline.getOportunidades(this._promotorId ? { comercialId: this._promotorId } : {}),
        ClienteService.instance.get().catch((error) => {
          console.warn("[PipelinePage._loadData] no se pudieron cargar los clientes", error);
          return [] as ClienteResponse[];
        }),
        PromotorService.instance.get().catch((error) => {
          console.warn("[PipelinePage._loadData] no se pudieron cargar los promotores", error);
          return [] as PromotorResponse[];
        }),
        // Una vez por sesión; si fallan, el pricing y las cards usan su fallback hardcodeado (los precios de Sextante).
        // Se vuelve a intentar al abrir de nuevo el Pipeline.
        pipeline.precios ? null : pipeline.loadPrecios(this._productos).catch((error) => {
          console.warn("[PipelinePage._loadData] no se pudieron cargar las tarifas del menú Costos", error);
          errorMessageComponent.addWarning("Precios no disponibles",
            "No se pudieron cargar las tarifas del menú Costos: las cotizaciones usan los precios de respaldo escritos en el código (los de Sextante). Se vuelve a intentar al abrir de nuevo el Pipeline.");
        }),
        pipeline.servicios ? null : pipeline.loadServicios().catch((error) => {
          console.warn("[PipelinePage._loadData] no se pudo cargar el catálogo de servicios; se usan los nombres del código", error);
        }),
      ]);
      this._ops        = opsRes.data;
      this._clientes   = clientes;
      this._promotores = promotores.filter(p => p.isActivo !== false);
      this._updateCount();
      this._renderKanban();
      this._renderLista();
      this._openPendingFicha();
    } catch (err) {
      errorMessageComponent.addError("Error al cargar el pipeline", (err as Error).message);
    }
  }

  /** Deep-link: abre una ficha concreta si otra página la solicitó (ej. Campañas → "Ver lead"). */
  private _openPendingFicha(): void {
    const pending = PipelinePage.pendingFicha;
    PipelinePage.pendingFicha = null;
    if (pending) this._openFicha(pending.clienteId, pending.opId);
  }

  private _updateCount(): void {
    const active = this._ops.filter(o => !CLOSED_STAGES.has(o.stage)).length;
    const uniqueClients = new Set(this._ops.map(o => o.clienteId)).size;
    const el = document.getElementById("pl-pg-su");
    const scope = this._promotorId ? " · solo tus oportunidades" : "";
    if (el) el.textContent = `· ${active} oportunidad${active !== 1 ? "es" : ""} activas · ${uniqueClients} cliente${uniqueClients !== 1 ? "s" : ""} registrados${scope}`;
  }

  private _cliente(id: string | null): ClienteResponse | undefined {
    if (!id) return undefined;
    return this._clientes.find(c => c.id === id);
  }

  // ── View switching ────────────────────────────────────────────────────────────

  private _switchView(name: string): void {
    document.querySelectorAll<HTMLElement>(".pl-view").forEach(v => v.classList.remove("active"));
    document.querySelectorAll<HTMLElement>(".pl-tab").forEach(t => t.classList.toggle("active", t.dataset.view === name));
    document.getElementById(`pl-view-${name}`)?.classList.add("active");

    if (name === "kanban")    this._renderKanban();
    if (name === "lista")     this._renderLista();
    if (name === "ficha")     void this._loadFicha();
    if (name === "dashboard") void this._renderDashboard();
    if (name === "agenda") {
      // Entrar al tab siempre recarga: mientras estabas fuera pudo crearse o editarse una op.
      // El caché de `_agendaData` existe solo para que cambiar de comercial no repita el GET.
      this._agendaKey = "";
      void this._renderAgenda();
    }
  }

  // ── VIEW 1: Kanban ─────────────────────────────────────────────────────────

  private _renderKanban(): void {
    const board = document.getElementById("pl-kanban-board");
    if (!board) return;
    board.innerHTML = "";

    STAGES.forEach(stage => {
      const ops = this._ops.filter(o => o.stage === stage.id);
      const c   = SC[stage.id];
      const isClosed = CLOSED_STAGES.has(stage.id);
      const col = document.createElement("div");
      col.className = "pl-kanban-col";
      col.innerHTML = `
        <div class="pl-kanban-col-hd" style="background:${c.bg};border:1px solid ${c.border}">
          <span class="pl-kanban-col-title" style="color:${c.text}">${stage.label.toUpperCase()}</span>
          <span class="pl-kanban-col-count" style="color:${c.text}">${ops.length}</span>
        </div>
        ${ops.map(o => this._kanbanCard(o)).join("")}
        ${isClosed ? "" : `<div class="pl-kanban-add" id="pl-add-${stage.id}">
          <i class="ph ph-plus"></i> Agregar
        </div>`}`;
      board.appendChild(col);
    });

    board.querySelectorAll<HTMLElement>(".pl-kanban-card").forEach(card => {
      card.onclick = () => this._openFicha(card.dataset.cid!, card.dataset.id!);
    });
    board.querySelectorAll<HTMLElement>(".pl-kanban-add").forEach(btn => {
      btn.onclick = () => this._openModal();
    });
  }

  private _kanbanCard(o: OportunidadResponse): string {
    const cli    = this._cliente(o.clienteId);
    const ha     = cli?.hectareasTotales ?? 0;
    const fincas = cli?.cantFincas ?? 0;
    const vc     = this._toMs(o.valorCierre);   // monto cotizado real (coacciona number|string|null → number|NaN)
    const pv     = this._calcDataAGForOp(o);
    const pvHtml = !isNaN(vc) && vc > 0
      ? `<div class="pl-card-value">$${(vc / 1_000_000).toFixed(1)}M cotizado</div>`
      : pv === "negociacion"
        ? `<div class="pl-card-negocia">Negociación</div>`
        : pv ? `<div class="pl-card-value">$${(pv / 1_000_000).toFixed(1)}M/año</div>` : "";
    const closedPill = o.stage === "closed_won"  ? `<span class="pl-op-badge won">✓ Ganado</span>`
                     : o.stage === "closed_lost" ? `<span class="pl-op-badge lost">✗ Perdido</span>` : "";
    const borderStyle = o.stage === "closed_won"  ? "border-left:3px solid #97C459;"
                      : o.stage === "closed_lost" ? "border-left:3px solid #B0AEA5;opacity:.8;" : "";
    return `<div class="pl-kanban-card" data-id="${o.id}" data-cid="${o.clienteId}" style="${borderStyle}">
      <div class="pl-card-name">${this._esc(o.clienteNombre)}</div>
      ${o.clienteNit ? `<div class="pl-card-meta"><i class="ph ph-identification-card"></i> ${this._esc(o.clienteNit)}</div>` : ""}
      ${fincas > 0 ? `<div class="pl-card-meta">
        <i class="ph ph-house"></i> ${fincas} finca${fincas !== 1 ? "s" : ""} · ${ha} ha
      </div>` : ""}
      <div class="pl-card-footer">
        ${closedPill || pvHtml}
        <span class="pl-src-badge ${SOURCE_CLASSES[o.source] ?? "src-manual"}">${SOURCE_LABELS[o.source] ?? "—"}</span>
      </div>
      <div class="pl-card-days">${this._daysAgo(o.createdAt)}</div>
    </div>`;
  }

  // ── VIEW 2: Lista ──────────────────────────────────────────────────────────

  /**
   * Pinta la vista Lista con las oportunidades que pasan los filtros: lo escrito en el buscador (cliente o NIT, con la
   * regla común de `busquedaTexto`: todas las palabras, sin distinguir mayúsculas ni tildes), la etapa y el origen.
   */
  private _renderLista(): void {
    const palabras = palabrasBusqueda((document.getElementById("pl-search") as HTMLInputElement)?.value ?? "");
    const fStg  = (document.getElementById("pl-filter-stage") as HTMLSelectElement)?.value ?? "";
    const fSrc  = (document.getElementById("pl-filter-source") as HTMLSelectElement)?.value ?? "";

    const filtered = this._ops.filter(o => {
      const matchQ  = coincideBusqueda(palabras, o.clienteNombre, o.clienteNit);
      const matchSt = !fStg || o.stage === fStg;
      const matchSr = !fSrc || o.source === fSrc;
      return matchQ && matchSt && matchSr;
    });

    const countEl = document.getElementById("pl-lista-count");
    if (countEl) countEl.textContent = `${filtered.length} resultado${filtered.length !== 1 ? "s" : ""}`;

    const tbody = document.getElementById("pl-lista-tbody");
    if (!tbody) return;

    if (filtered.length === 0) {
      tbody.innerHTML = `<tr><td colspan="9" class="pl-empty">Sin resultados</td></tr>`;
      return;
    }

    tbody.innerHTML = filtered.map(o => {
      const cli = this._cliente(o.clienteId);
      const c   = SC[o.stage] ?? SC["new"];
      const pv  = this._calcDataAGForOp(o);
      const pvStr = pv === "negociacion" ? "Negociación" : pv ? `$${(pv / 1_000_000).toFixed(1)}M/año` : "—";
      const overdue = this._isOverdue(o.proxContacto) && !CLOSED_STAGES.has(o.stage);
      return `<tr class="pl-lista-row" data-id="${o.id}" data-cid="${o.clienteId}">
        <td>
          <div class="pl-td-name">${this._esc(o.clienteNombre)}</div>
          <div class="pl-td-sub">${this._esc(o.clienteNit ?? "—")}</div>
        </td>
        <td><span class="pl-src-badge ${SOURCE_CLASSES[o.source] ?? "src-manual"}">${SOURCE_LABELS[o.source] ?? "—"}</span></td>
        <td class="pl-td-num">${cli?.hectareasTotales ?? 0} ha</td>
        <td class="pl-td-sub">${this._esc(this._buildProductoStr(o.productos))}</td>
        <td><span class="pl-stage-badge" style="background:${c.bg};color:${c.text};border:1px solid ${c.border}">${getStageLabel(o.stage)}</span></td>
        <td class="pl-td-num">${pvStr}</td>
        <td class="pl-td-sub" style="color:${overdue ? "var(--sx-error)" : "inherit"}">${o.proxContacto ? this._fmtDate(o.proxContacto) : "—"}</td>
        <td class="pl-td-sub">${this._esc(o.comercialNombre ?? "—")}</td>
        <td class="pl-td-actions" onclick="event.stopPropagation()">
          <button class="sx-btn sx-btn-outline sx-btn-sm pl-del-btn" data-id="${o.id}" title="Eliminar">
            <i class="ph ph-trash-simple"></i>
          </button>
        </td>
      </tr>`;
    }).join("");

    tbody.querySelectorAll<HTMLElement>(".pl-lista-row").forEach(row => {
      row.onclick = () => this._openFicha(row.dataset.cid!, row.dataset.id!);
    });
    tbody.querySelectorAll<HTMLButtonElement>(".pl-del-btn").forEach(btn => {
      btn.onclick = () => void this._deleteOportunidad(btn.dataset.id!);
    });
  }

  // ── VIEW 3: Ficha ─────────────────────────────────────────────────────────

  private _openFicha(clienteId: string, opId?: string): void {
    this._currentClienteId = clienteId;
    this._currentOpId      = opId ?? null;
    // El tab Ficha está oculto hasta que se abre una oportunidad (igual que el mockup).
    const fichaTab = document.getElementById("pl-tab-ficha");
    if (fichaTab) fichaTab.style.display = "";
    this._switchView("ficha");
  }

  private _backToKanban(): void {
    const fichaTab = document.getElementById("pl-tab-ficha");
    if (fichaTab) fichaTab.style.display = "none";
    this._switchView("kanban");
  }

  private _loadFicha(): void {
    const msg     = document.getElementById("pl-ficha-msg");
    const content = document.getElementById("pl-ficha-content");
    if (!content) return;

    if (!this._currentClienteId) {
      if (msg) msg.style.display = "flex";
      content.innerHTML = "";
      return;
    }
    if (msg) msg.style.display = "none";

    // Render inmediato desde memoria (las ops ya se cargaron en _loadData).
    this._clienteOps = this._ops
      .filter(o => o.clienteId === this._currentClienteId)
      .slice()
      .sort((a, b) => b.createdAt - a.createdAt);
    this._selectFichaOp();
    this._fichaFincas = [];
    this._renderFichaContent();

    // Refrescos del servidor en segundo plano (no bloquean; toleran endpoint no disponible).
    void this._refreshClienteOps();
    void this._refreshFichaFincas();
  }

  /**
   * Trae las fincas del cliente para tarifar la ficha con las hectáreas reales.
   * Misma fuente que `_openCotizarDialog`/`_downloadOpPdf`, para que la ficha y la
   * cotización nunca muestren precios distintos.
   */
  private async _refreshFichaFincas(): Promise<void> {
    const clienteId = this._currentClienteId;
    if (!clienteId) return;
    try {
      const raw = await FincaListByClienteIdService.instance(clienteId).get();
      if (this._currentClienteId !== clienteId) return;  // cambió de ficha mientras cargaba
      this._fichaFincas = raw.filter(f => !f.isEliminado);
      this._renderFichaContent();
    } catch (error) {
      // Sin conexión: se conserva lo renderizado con los datos del cliente cacheado.
      console.warn(`[PipelinePage._refreshFichaFincas] no se pudieron actualizar las fincas del cliente ${clienteId}`, error);
    }
  }

  /**
   * Hectáreas y perfil del cliente de la ficha. Prefiere las fincas recién cargadas;
   * cae a los totales del cliente mientras llegan (o si el endpoint falló).
   */
  private _fichaHa(cli?: ClienteResponse): { ha: number; esGan: boolean; cant: number } {
    const fincas = this._fichaFincas;
    if (fincas.length > 0) {
      const ha    = fincas.reduce((s, f) => s + (f.hectareasFinca ?? 0), 0);
      const ganHa = fincas.reduce((s, f) => s + (f.isGanaderia ? (f.hectareasFinca ?? 0) : 0), 0);
      return { ha, esGan: ganHa > ha / 2, cant: fincas.length };
    }
    return { ha: cli?.hectareasTotales ?? 0, esGan: false, cant: cli?.cantFincas ?? 0 };
  }

  /** Selecciona la op a mostrar: la indicada, o la activa más reciente, o la primera. */
  private _selectFichaOp(): void {
    if (!this._currentOpId || !this._clienteOps.some(o => o.id === this._currentOpId)) {
      const activa = this._clienteOps.find(o => !CLOSED_STAGES.has(o.stage));
      this._currentOpId = (activa ?? this._clienteOps[0])?.id ?? null;
    }
  }

  /** Trae las ops frescas del cliente y re-renderiza si sigue en la misma ficha. */
  private async _refreshClienteOps(): Promise<void> {
    const clienteId = this._currentClienteId;
    if (!clienteId) return;
    try {
      const res = await PipelineService.instance.getOportunidades({ clienteId });
      if (this._currentClienteId !== clienteId) return;  // cambió de ficha mientras cargaba
      if (!res?.data) return;
      this._clienteOps = res.data.slice().sort((a, b) => b.createdAt - a.createdAt);
      this._selectFichaOp();
      this._renderFichaContent();
    } catch (error) {
      // Endpoint no disponible o sin conexión: se conserva lo renderizado desde memoria.
      console.warn(`[PipelinePage._refreshClienteOps] no se pudieron actualizar las oportunidades del cliente ${clienteId}`, error);
    }
  }

  private _renderFichaContent(): void {
    const content = document.getElementById("pl-ficha-content");
    if (!content) return;

    const cli = this._cliente(this._currentClienteId);
    const op  = this._clienteOps.find(o => o.id === this._currentOpId) ?? null;

    const nombre = cli?.nombre ?? op?.clienteNombre ?? "Cliente";
    const nit    = cli?.nit ?? op?.clienteNit ?? "—";
    const { ha: fichaHa, esGan, cant: fichaCant } = this._fichaHa(cli);
    const pvCli  = this._calcDataAGFromHa(fichaHa, esGan);
    const pvStr  = pvCli === "negociacion" ? "Negociación directa" : pvCli ? `$${(pvCli / 1_000_000).toFixed(2)}M/año` : "—";
    const fincasPill = this._fichaFincas;

    content.innerHTML = `
      <div class="pl-ficha-back" id="pl-ficha-back">
        <i class="ph ph-arrow-left" aria-hidden="true"></i> Volver al kanban
      </div>
      <div class="pl-ficha-grid">
        <div class="pl-ficha-left">
          <div class="pl-ficha-card">
            <div class="pl-ficha-avatar-row">
              <div class="pl-ficha-avatar">${nombre.charAt(0).toUpperCase()}</div>
              <div>
                <div class="pl-ficha-nombre">${this._esc(nombre)}</div>
                <div class="pl-ficha-nit">NIT ${this._esc(nit)}</div>
              </div>
            </div>
            <hr class="pl-divider">
            ${cli ? `
              <div class="pl-ficha-field"><span>Teléfono</span><span>${this._esc(cli.telefono || "—")}</span></div>
              <div class="pl-ficha-field"><span>Fincas</span><span>${fichaCant} · ${fichaHa} ha</span></div>
              <div class="pl-ficha-field"><span>Valor DATA AG</span><span>${pvStr}</span></div>
              ${fincasPill.length > 0 ? `<div style="margin-top:8px;display:flex;flex-wrap:wrap;gap:4px">
                ${fincasPill.map(f => `<span class="pl-finca-pill">🌱 ${this._esc(f.nombreFinca)} · ${f.hectareasFinca} ha</span>`).join("")}
              </div>` : ""}
            ` : `<div class="pl-ficha-field"><span>Datos</span><span style="color:var(--sx-text-tertiary)">No sincronizados</span></div>`}
            <button class="sx-btn sx-btn-primary sx-btn-sm" style="width:100%;margin-top:12px" id="pl-ficha-nueva-op">
              <i class="ph ph-plus"></i> Nueva oportunidad
            </button>
          </div>

          <div class="pl-ficha-card">
            <div class="pl-ficha-sec">Oportunidades (${this._clienteOps.length})</div>
            <div class="pl-op-list">
              ${this._clienteOps.length === 0
                ? `<div class="pl-empty">Sin oportunidades</div>`
                : this._clienteOps.map(o => this._opListItem(o)).join("")}
            </div>
          </div>
        </div>

        <div class="pl-ficha-right" id="pl-ficha-op">
          ${op ? this._renderOpDetail(op, this._buildProductosPricingHtml(op.productos, fichaHa, esGan, this._valorCierreNum(op) != null)) : `<div class="pl-ficha-card"><div class="pl-empty">Selecciona una oportunidad</div></div>`}
        </div>
      </div>
    `;

    // Wiring
    document.getElementById("pl-ficha-back")!.onclick = () => this._backToKanban();
    document.getElementById("pl-ficha-nueva-op")!.onclick = () => {
      if (cli) this._openModalForCliente({ id: cli.id, nombre: cli.nombre, nit: cli.nit, telefono: cli.telefono, hectareasTotales: fichaHa, ubicacion: null, nucleoAsignado: null, proyectosActivos: 0 });
      else this._openModal();
    };
    content.querySelectorAll<HTMLElement>(".pl-op-item").forEach(item => {
      item.onclick = () => { this._currentOpId = item.dataset.id!; this._renderFichaContent(); };
    });
    if (op) this._wireOpDetail(op);
  }

  private _opListItem(o: OportunidadResponse): string {
    const c = SC[o.stage] ?? SC["new"];
    const active = o.id === this._currentOpId ? "active" : "";
    const badge = o.stage === "closed_won"  ? `<span class="pl-op-badge won">✓ GANADO</span>`
                : o.stage === "closed_lost" ? `<span class="pl-op-badge lost">✗ PERDIDO</span>` : "";
    return `<div class="pl-op-item ${active}" data-id="${o.id}">
      <span class="pl-op-dot" style="background:${c.bar}"></span>
      <div class="pl-op-item-body">
        <div class="pl-op-item-top">
          <span class="pl-op-item-stage">${getStageLabel(o.stage)}</span>
          ${badge}
        </div>
        <div class="pl-op-item-meta">${SOURCE_LABELS[o.source] ?? o.source} · ${this._esc(o.comercialNombre ?? "Sin asignar")} · ${this._fmtTs(o.createdAt)}</div>
      </div>
    </div>`;
  }

  /**
   * `valorCierre` como número, o null si la op aún no tiene cotización generada.
   * Es int64 y puede llegar como string, de ahí el `_toMs` (coacciona number|string).
   */
  private _valorCierreNum(o: OportunidadResponse): number | null {
    const vc = this._toMs(o.valorCierre);
    return !isNaN(vc) && vc > 0 ? vc : null;
  }

  private _renderOpDetail(o: OportunidadResponse, productosHtml?: string): string {
    const c = SC[o.stage] ?? SC["new"];
    const closed = CLOSED_STAGES.has(o.stage);
    const vc = this._valorCierreNum(o);
    const vcHtml = vc != null
      ? `<div class="pl-ficha-field"><span>Valor cotizado</span><span style="font-weight:600">$${vc.toLocaleString("es-CO")}</span></div>`
      : "";
    return `
      <div class="pl-ficha-card">
        <div class="pl-op-detail-hd">
          <span class="pl-stage-badge" style="background:${c.bg};color:${c.text};border:1px solid ${c.border}">${getStageLabel(o.stage)}</span>
          <span class="pl-src-badge ${SOURCE_CLASSES[o.source] ?? "src-manual"}">${SOURCE_LABELS[o.source] ?? "—"}</span>
          <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" id="pl-op-edit" title="Editar oportunidad" style="margin-left:auto"><i class="ph ph-pencil"></i></button>
        </div>

        <div class="pl-ficha-field"><span>Comercial</span><span>${this._esc(o.comercialNombre ?? "Sin asignar")}</span></div>
        ${productosHtml ?? `<div class="pl-ficha-field"><span>Productos</span><span>${this._esc(this._buildProductoStr(o.productos))}</span></div>`}
        ${vcHtml}
        ${o.proxContacto ? `<div class="pl-ficha-field"><span>Próx. contacto</span><span style="${this._isOverdue(o.proxContacto) && !closed ? "color:var(--sx-error);font-weight:600" : ""}">${this._fmtDate(o.proxContacto)}</span></div>` : ""}
        ${o.stage === "closed_won" && o.activatedAt  ? `<div class="pl-ficha-field"><span>Activado</span><span style="color:var(--sx-success-text);font-weight:600">${this._fmtDate(o.activatedAt)}</span></div>` : ""}
        ${o.stage === "closed_won" && o.renovationAt ? `<div class="pl-ficha-field"><span>Renovación</span><span>${this._fmtDate(o.renovationAt)} · <b style="color:var(--sx-warning-text)">${this._daysUntil(o.renovationAt)}</b></span></div>` : ""}
        ${o.notas ? `<hr class="pl-divider"><div class="pl-ficha-field" style="align-items:flex-start"><span>Notas</span><span style="color:var(--sx-text-secondary);line-height:1.5">${this._esc(o.notas)}</span></div>` : ""}

        ${o.stage === "demo" ? `
          <hr class="pl-divider">
          <div class="pl-ficha-sec">Demo de plataforma</div>
          ${o.demoFin ? `
            <div class="pl-ficha-field"><span>Demo hasta</span><span>${this._fmtDate(o.demoFin)} · <b style="color:var(--sx-warning-text)">${this._daysUntil(o.demoFin)}</b></span></div>
            <div style="display:flex;gap:6px;margin-top:8px">
              <input class="sx-input" id="pl-demo-dias" type="number" min="1" value="15" style="width:78px">
              <button class="sx-btn sx-btn-outline sx-btn-sm" style="flex:1" id="pl-demo-extender"><i class="ph ph-clock-clockwise"></i> Extender demo</button>
            </div>
          ` : `
            <button class="sx-btn sx-btn-primary sx-btn-sm" style="width:100%" id="pl-demo-asignar"><i class="ph ph-play"></i> Asignar demo (30 días)</button>
          `}
        ` : ""}

        ${o.stage === "closed_won" ? `
          <hr class="pl-divider">
          <div class="pl-ficha-sec">Renovar</div>
          <input class="sx-input" id="pl-renov-fecha" type="date" style="margin-bottom:8px"${o.renovationAt ? ` value="${o.renovationAt.slice(0, 10)}"` : ""}>
          <button class="sx-btn sx-btn-primary sx-btn-sm" style="width:100%" id="pl-renov-btn"><i class="ph ph-arrows-clockwise"></i> Renovar oportunidad</button>
        ` : ""}

        ${closed ? "" : `
          <hr class="pl-divider">
          <div class="pl-ficha-sec">Cambiar etapa</div>
          <select class="sx-input" id="pl-stage-sel" style="margin-bottom:8px">
            ${STAGES.map(s => `<option value="${s.id}" ${s.id === o.stage ? "selected" : ""}>${s.label}</option>`).join("")}
          </select>
          <button class="sx-btn sx-btn-primary sx-btn-sm" style="width:100%" id="pl-stage-btn">Actualizar etapa</button>
        `}

        ${o.stage === "proposal" ? `
          <button class="sx-btn sx-btn-primary sx-btn-sm" style="width:100%;margin-top:12px" id="pl-op-cotizar">
            <i class="ph ph-file-text" aria-hidden="true"></i> Generar cotización
          </button>
          <div class="pl-hint" style="margin-top:6px">Configura productos y genera el PDF de la cotización.</div>
        ` : ""}

        ${o.stage === "proposal" || closed ? `
          <button class="sx-btn sx-btn-outline sx-btn-sm" style="width:100%;margin-top:12px" id="pl-op-pdf">
            <i class="ph ph-download-simple" aria-hidden="true"></i> Descargar cotización PDF
          </button>
        ` : ""}
      </div>

      <div class="pl-ficha-card">
        <div class="pl-ficha-sec">Historial</div>
        <div class="pl-timeline">
          ${o.eventos.length === 0
            ? `<div class="pl-empty">Sin eventos</div>`
            : o.eventos.map(ev => `
              <div class="pl-tl-item">
                <div class="pl-tl-dot ${ev.tipo}"></div>
                <div class="pl-tl-body">
                  <div class="pl-tl-title">${this._esc(renderEventoDesc(ev))}</div>
                  <div class="pl-tl-meta">${this._esc(ev.usuarioNombre ?? "Sistema")}</div>
                </div>
                <div class="pl-tl-date">${ev.fecha.slice(0, 16).replace("T", " ")}</div>
              </div>`).join("")}
        </div>

        ${closed ? "" : `
          <div class="pl-note-form">
            <textarea class="sx-input" id="pl-note-inp" rows="2" placeholder="Registrar interacción, llamada, acuerdo…" style="resize:vertical;margin-bottom:8px"></textarea>
            <button class="sx-btn sx-btn-outline sx-btn-sm" style="width:100%" id="pl-note-btn">
              <i class="ph ph-plus"></i> Agregar nota
            </button>
          </div>
        `}
      </div>
    `;
  }

  private _wireOpDetail(o: OportunidadResponse): void {
    const editBtn = document.getElementById("pl-op-edit");
    if (editBtn) editBtn.onclick = () => this._openEditOpModal(o);
    const stageBtn = document.getElementById("pl-stage-btn");
    if (stageBtn) stageBtn.onclick = () => void this._changeStage(o.id);
    const noteBtn = document.getElementById("pl-note-btn");
    if (noteBtn) noteBtn.onclick = () => {
      const txt = (document.getElementById("pl-note-inp") as HTMLTextAreaElement).value.trim();
      if (txt) void this._addNota(o.id, txt);
    };
    const pdfBtn = document.getElementById("pl-op-pdf");
    if (pdfBtn) pdfBtn.onclick = () => void this._downloadOpPdf(o);
    const cotizarBtn = document.getElementById("pl-op-cotizar");
    if (cotizarBtn) cotizarBtn.onclick = () => void this._openCotizarDialog(o);
    const demoAsignar = document.getElementById("pl-demo-asignar");
    if (demoAsignar) demoAsignar.onclick = () => void this._asignarDemo(o.id);
    const demoExtender = document.getElementById("pl-demo-extender");
    if (demoExtender) demoExtender.onclick = () => void this._extenderDemo(o.id);
    const renovBtn = document.getElementById("pl-renov-btn");
    if (renovBtn) renovBtn.onclick = () => void this._renovarOp(o.id);
  }

  private async _asignarDemo(id: string): Promise<void> {
    try {
      this._replaceOp(await PipelineService.instance.asignarDemo(id));
      this._renderFichaContent();
    } catch (err) {
      errorMessageComponent.addError("Error al asignar la demo", (err as Error).message);
    }
  }

  private async _extenderDemo(id: string): Promise<void> {
    const dias = parseInt((document.getElementById("pl-demo-dias") as HTMLInputElement | null)?.value ?? "") || 15;
    try {
      const payload = new ExtenderDemoPayload();
      payload.dias = dias;
      this._replaceOp(await PipelineService.instance.extenderDemo(id, payload));
      this._renderFichaContent();
    } catch (err) {
      errorMessageComponent.addError("Error al extender la demo", (err as Error).message);
    }
  }

  private async _renovarOp(id: string): Promise<void> {
    const raw = (document.getElementById("pl-renov-fecha") as HTMLInputElement | null)?.value ?? "";
    if (!raw) { alert("Selecciona la fecha de renovación."); return; }
    try {
      const payload = new RenovarOportunidadPayload();
      payload.renovationAt = new Date(raw + "T12:00:00").toISOString();
      this._replaceOp(await PipelineService.instance.renovarOportunidad(id, payload));
      this._renderFichaContent();
    } catch (err) {
      errorMessageComponent.addError("Error al renovar", (err as Error).message);
    }
  }

  // ── VIEW 4: Agenda ─────────────────────────────────────────────────────────

  /** Carga la agenda del mes (solo si cambió) y la pinta con el filtro de comercial vigente. */
  private async _renderAgenda(): Promise<void> {
    const grid  = document.getElementById("pl-agenda-grid");
    const label = document.getElementById("pl-agenda-month-label");
    if (!grid) return;

    const m = this._agendaMonth;
    if (label) label.textContent = m.toLocaleDateString("es-CO", { month: "long", year: "numeric" });
    this._renderAgendaFilter();

    const key = `${m.getFullYear()}-${m.getMonth()}`;
    if (this._agendaKey !== key || !this._agendaData) {
      grid.innerHTML = `<div class="pl-empty" style="grid-column:1/-1">Cargando…</div>`;
      const desde = new Date(m.getFullYear(), m.getMonth(), 1).toISOString();
      const hasta = new Date(m.getFullYear(), m.getMonth() + 1, 0, 23, 59, 59).toISOString();
      try {
        this._agendaData = await PipelineService.instance.getAgenda(desde, hasta);
        this._agendaKey  = key;
      } catch (error) {
        console.warn(`[PipelinePage._renderAgenda] no se pudo cargar la agenda de ${key}`, error);
        this._agendaData = null;
        this._agendaKey  = "";
        grid.innerHTML = `<div class="pl-empty" style="grid-column:1/-1;color:var(--sx-error)">Error al cargar agenda</div>`;
        return;
      }
    }
    this._paintAgenda(grid);
  }

  /** Select de comercial de la agenda. Se repinta en cada render: `selectedId` conserva la elección. */
  private _renderAgendaFilter(): void {
    const sel = document.getElementById("pl-agenda-comercial") as HTMLSelectElement | null;
    if (!sel) return;
    sel.innerHTML = this._promotorOptions(this._agendaComercialId, "Toda la agenda");
    sel.onchange = () => {
      this._agendaComercialId = sel.value || null;
      void this._renderAgenda();   // usa el caché del mes, no vuelve a pegarle al API
    };
  }

  /** Pinta la agenda ya cargada aplicando el filtro de comercial (el endpoint no lo soporta). */
  private _paintAgenda(grid: HTMLElement): void {
    const data = this._agendaData;
    if (!data) return;

    const cid = this._agendaComercialId;
    const f = (list: OportunidadResponse[] | null): OportunidadResponse[] =>
      cid ? (list ?? []).filter(o => o.comercialId === cid) : (list ?? []);
    const demos      = f(data.demos);
    const pendientes = f(data.pendientes);
    const vencidos   = f(data.vencidos);
    const suf = cid ? " para este comercial" : "";

    grid.innerHTML = `
      <div>
        <div class="pl-agenda-sec">Demos programadas (${demos.length})</div>
        ${demos.length === 0
          ? `<div class="pl-empty">Sin demos este mes${suf}</div>`
          : demos.map(o => this._agendaCard(o, false)).join("")}
      </div>
      <div>
        <div class="pl-agenda-sec">Seguimientos</div>
        ${vencidos.length > 0 ? `<div class="pl-agenda-urgent-lbl">VENCIDOS (${vencidos.length})</div>${vencidos.map(o => this._agendaCard(o, true)).join("")}` : ""}
        ${pendientes.length > 0 ? `<div class="pl-agenda-upcoming-lbl">PRÓXIMOS (${pendientes.length})</div>${pendientes.map(o => this._agendaCard(o, false)).join("")}` : ""}
        ${vencidos.length === 0 && pendientes.length === 0 ? `<div class="pl-empty">Sin pendientes${suf} ✓</div>` : ""}
      </div>
    `;

    grid.querySelectorAll<HTMLElement>(".pl-agenda-card").forEach(card => {
      card.onclick = () => this._openFicha(card.dataset.cid!, card.dataset.id!);
    });
  }

  private _agendaCard(o: OportunidadResponse, urgent: boolean): string {
    const cli = this._cliente(o.clienteId);
    return `<div class="pl-agenda-card ${urgent ? "urgent" : ""}" data-id="${o.id}" data-cid="${o.clienteId}">
      <div class="pl-agenda-time" style="${urgent ? "color:var(--sx-error)" : ""}">
        ${o.proxContacto ? this._fmtDate(o.proxContacto) + " · " + this._daysUntil(o.proxContacto) : "—"}
      </div>
      <div class="pl-agenda-name">${this._esc(o.clienteNombre)}</div>
      <div class="pl-agenda-meta">${this._esc(cli?.telefono ?? "")} · ${getStageLabel(o.stage)}</div>
    </div>`;
  }

  // ── VIEW 5: Dashboard ──────────────────────────────────────────────────────

  /**
   * Pinta el tablero del Pipeline: los indicadores y el embudo que entrega el Server (`getStats`) y el valor del
   * pipeline abierto, calculado con el precio de DATA AG de cada oportunidad. Si las estadísticas no cargan, lo dice
   * en el panel y escribe el error en consola.
   */
  private async _renderDashboard(): Promise<void> {
    const el = document.getElementById("pl-dash-content");
    if (!el) return;
    el.innerHTML = `<div class="pl-empty">Cargando estadísticas…</div>`;

    try {
      const stats: PipelineStatsResponse = await PipelineService.instance.getStats();

      const valorPipeline = this._ops
        .filter(o => !CLOSED_STAGES.has(o.stage))
        .reduce((acc, o) => { const v = this._calcDataAGForOp(o); return acc + (typeof v === "number" ? v : 0); }, 0);

      const maxCount = Math.max(...stats.embudo.map(e => e.count), 1);

      el.innerHTML = `
        <div class="pl-dash-kpis">
          ${this._kpi(String(stats.oportunidadesActivas), "OPORTUNIDADES ACTIVAS", "neutral")}
          ${this._kpi(String(stats.demosAgendadas),       "DEMOS AGENDADAS",       "blue")}
          ${this._kpi(String(stats.cerradasGanadas),      "GANADOS",               "green")}
          ${this._kpi(String(stats.cerradasPerdidas),     "PERDIDOS",              "red")}
          ${this._kpi(stats.tasaConversion.toFixed(0) + "%", "CONVERSIÓN",         "neutral")}
          ${this._kpi(valorPipeline > 0 ? "$" + (valorPipeline / 1_000_000).toFixed(1) + "M" : "$0", "VALOR PIPELINE", "neutral")}
        </div>

        <div class="pl-dash-row">
          <div class="pl-dash-panel">
            <div class="pl-dash-panel-title">Embudo de conversión</div>
            ${stats.embudo.map(e => `
              <div class="pl-funnel-row">
                <span class="pl-funnel-lbl">${e.label}</span>
                <div class="pl-funnel-bar-wrap">
                  <div class="pl-funnel-bar" style="width:${Math.round(e.count / maxCount * 100)}%;background:${SC[e.stage]?.bar ?? "#ccc"}"></div>
                </div>
                <span class="pl-funnel-count">${e.count}</span>
              </div>`).join("")}
          </div>

          <div class="pl-dash-panel">
            <div class="pl-dash-panel-title">Oportunidades por fuente</div>
            ${stats.porFuente.map(f => {
              const total = stats.porFuente.reduce((a, x) => a + x.count, 0) || 1;
              return `<div class="pl-dash-row-item">
                <span class="pl-src-badge ${SOURCE_CLASSES[f.source] ?? "src-manual"}">${SOURCE_LABELS[f.source] ?? f.source}</span>
                <span class="pl-dash-count">${f.count}</span>
                <span class="pl-dash-pct">${Math.round(f.count / total * 100)}%</span>
              </div>`;
            }).join("") || `<div class="pl-empty">Sin datos</div>`}
          </div>
        </div>

        <div class="pl-dash-row">
          <div class="pl-dash-panel">
            <div class="pl-dash-panel-title">Rendimiento por comercial</div>
            ${stats.rendimientoComerciales.map(r => {
              const tasa = r.leads > 0 ? Math.round(r.cerrados / r.leads * 100) : 0;
              return `<div class="pl-dash-row-item">
                <span class="pl-dash-name">${this._esc(r.comercial)}</span>
                <span class="pl-dash-count">${r.leads} ops · ${r.cerrados} ganada${r.cerrados !== 1 ? "s" : ""}</span>
                <span class="pl-dash-pct">${tasa}%</span>
              </div>`;
            }).join("") || `<div class="pl-empty">Sin datos</div>`}
          </div>
        </div>
      `;
    } catch (error) {
      console.warn("[PipelinePage._renderDashboard] no se pudieron cargar las estadísticas del Pipeline", error);
      el.innerHTML = `<div class="pl-empty" style="color:var(--sx-error)">Error al cargar estadísticas</div>`;
    }
  }

  private _kpi(value: string, label: string, color: "neutral" | "green" | "blue" | "red"): string {
    return `<div class="pl-kpi">
      <div class="pl-kpi-val pl-kpi-${color}">${value}</div>
      <div class="pl-kpi-lbl">${label}</div>
    </div>`;
  }

  // ── Cotización: acción sobre una oportunidad (diálogo de confirmación) ───────

  /**
   * Abre el diálogo de cotización de una oportunidad: usa sus productos ya capturados
   * + las fincas del cliente, calcula el total y solo pide el próximo contacto.
   */
  private async _openCotizarDialog(op: OportunidadResponse): Promise<void> {
    // Fincas reales del cliente (read-only; alimentan el precio DATA AG)
    let fincas: CotizFinca[] = [];
    try {
      const raw = await FincaListByClienteIdService.instance(op.clienteId).get();
      fincas = raw.filter(f => !f.isEliminado).map((f, i) => ({
        id: i + 1,
        fincaId: f.id,
        nombre: f.nombreFinca,
        tipo: f.isGanaderia ? "ganadería" : (f.tipo?.toLowerCase() ?? "agricultura"),
        municipio: f.municipioNombre ?? "",
        ha: f.hectareasFinca,
      }));
    } catch (error) {
      // Sin esto se vería como un cliente sin fincas, y DATA AG quedaría sin precio.
      console.warn(`[PipelinePage._openCotizarDialog] no se pudieron cargar las fincas del cliente ${op.clienteId}`, error);
      errorMessageComponent.addError("Error al cargar las fincas", (error as Error).message);
    }

    const totalHa = fincas.reduce((s, f) => s + f.ha, 0);
    // Productos precargados de la op; editables en el diálogo. Sin los que la marca no vende.
    const prods: CotizProds = op.productos
      ? this._productos.filtrar(op.productos)
      : this._defaultCotizProds();
    if (!prods.protec.ha || prods.protec.ha <= 0) prods.protec.ha = totalHa;
    const ganHa = fincas.filter(f => f.tipo.startsWith("ganader")).reduce((s, f) => s + f.ha, 0);
    // Config de VISTA (km, tipo, conteo) — no viene en op.productos; se captura aquí.
    const vistaCfg = { km: 0, tipo: ganHa > totalHa / 2 ? "ganadería" : "agricultura", arboles: "no" };

    const cli     = this._cliente(op.clienteId);
    const nombre  = cli?.nombre ?? op.clienteNombre ?? "Cliente";
    const nit     = cli?.nit ?? op.clienteNit ?? "";
    const proxVal = op.proxContacto ? op.proxContacto.slice(0, 10) : "";

    const chk = "display:flex;align-items:center;gap:8px;font-size:14px;cursor:pointer";
    const row = "display:flex;align-items:center;justify-content:space-between;gap:8px";
    const lbl = "font-size:13px;color:var(--sx-text-secondary)";
    const num = "max-width:130px;padding:5px 8px";
    const cfg = "padding:8px 10px;background:var(--sx-bg-subtle);border-radius:8px;display:flex;flex-direction:column;gap:8px";

    document.getElementById("pl-modal-title")!.textContent = "Generar cotización";
    document.getElementById("pl-modal-content")!.innerHTML = `
      <div class="pl-modal-body">
        <div class="pl-cotiz-cli-card" style="margin-bottom:12px">
          <div>
            <div class="pl-cotiz-cli-name">${this._esc(nombre)}</div>
            <div class="pl-cotiz-cli-sub" id="cz-cli-sub">NIT ${this._esc(nit)} · ${fincas.length} finca${fincas.length !== 1 ? "s" : ""} · ${totalHa} ha</div>
          </div>
        </div>

        <label class="pl-label" style="display:block;margin-bottom:6px">Fincas del cliente <span style="font-weight:400;color:var(--sx-text-tertiary)">(la suma de hectáreas define el precio DATA AG)</span></label>
        <div id="cz-fincas-list" style="display:flex;flex-direction:column;gap:6px;margin-bottom:8px"></div>
        <div style="${cfg};margin-bottom:12px">
          <div style="${row};flex-wrap:wrap">
            <input id="cz-fnc-nombre" class="sx-input" placeholder="Nombre finca" style="flex:1;min-width:130px;padding:5px 8px">
            <select id="cz-fnc-tipo" class="sx-input" style="${num}"><option value="0">Agricultura</option><option value="1">Ganadería</option></select>
            <input id="cz-fnc-ha" class="sx-input" type="number" min="0" step="1" placeholder="Ha" style="max-width:80px;padding:5px 8px">
          </div>
          <div style="${row};flex-wrap:wrap">
            <select id="cz-fnc-depto" class="sx-input" style="flex:1;min-width:130px;padding:5px 8px"><option value="">Cargando…</option></select>
            <select id="cz-fnc-muni" class="sx-input" style="flex:1;min-width:130px;padding:5px 8px" disabled><option value="">— Selecciona departamento —</option></select>
          </div>
          <button class="sx-btn sx-btn-outline sx-btn-sm" id="cz-fnc-add" style="align-self:flex-start"><i class="ph ph-plus"></i> Agregar finca</button>
        </div>

        <label class="pl-label" style="display:block;margin-bottom:8px">Productos y servicios</label>
        <div style="display:flex;flex-direction:column;gap:10px;margin-bottom:12px">
          <label style="${chk}"><input type="checkbox" id="cz-dataag" ${prods.dataag ? "checked" : ""}> DATA AG — suscripción anual (según hectáreas)</label>

          ${this._productos.ofrece("rastro") ? `<div style="${row}"><span style="${lbl}">RASTRO GPS — unidades</span><input id="cz-rastro" class="sx-input" type="number" min="0" step="1" value="${prods.rastro || ""}" placeholder="0" style="${num}"></div>` : ""}
          ${this._productos.ofrece("bascula") ? `<div style="${row}"><span style="${lbl}">Báscula — unidades</span><input id="cz-bascula" class="sx-input" type="number" min="0" step="1" value="${prods.bascula || ""}" placeholder="0" style="${num}"></div>` : ""}

          ${this._productos.ofrece("vista") ? `<label style="${chk}"><input type="checkbox" id="cz-vista" ${prods.vista ? "checked" : ""}> VISTA — fotogrametría / visita</label>
          <div id="cz-vista-cfg" style="${cfg}" ${prods.vista ? "" : "hidden"}>
            <div style="${row}"><span style="${lbl}">Km desde Pereira</span><input id="cz-vista-km" class="sx-input" type="number" min="0" step="1" value="${vistaCfg.km}" style="${num}"></div>
            <div style="${row}"><span style="${lbl}">Tipo de vuelo</span><select id="cz-vista-tipo" class="sx-input" style="${num}"><option value="agricultura">Agricultura</option><option value="ganadería">Ganadería</option></select></div>
            <div style="${row}"><span style="${lbl}">Conteo de árboles</span><select id="cz-vista-arb" class="sx-input" style="${num}"><option value="no">No</option><option value="si">Sí</option></select></div>
          </div>` : ""}

          ${this._productos.ofrece("protec") ? `<label style="${chk}"><input type="checkbox" id="cz-protec" ${prods.protec.activo ? "checked" : ""}> PROTEC — aspersión</label>
          <div id="cz-protec-cfg" style="${cfg}" ${prods.protec.activo ? "" : "hidden"}>
            <div style="${row}"><span style="${lbl}">Categoría de cultivo</span><select id="cz-protec-cat" class="sx-input" style="${num}"><option value="">Selecciona…</option>${this._buildProtecOptions()}</select></div>
            <div style="${row}"><span style="${lbl}">Hectáreas (mín. 10)</span><input id="cz-protec-ha" class="sx-input" type="number" min="0" step="1" value="${prods.protec.ha || ""}" style="${num}"></div>
          </div>` : ""}
        </div>

        <div id="cz-live" style="margin-bottom:12px"></div>

        <label class="pl-label">Notas</label>
        <textarea id="cz-notas" class="sx-input" rows="2" style="resize:vertical;margin-bottom:12px">${this._esc(op.notas ?? "")}</textarea>

        <label class="pl-label">Próximo contacto</label>
        <input id="pl-cotizar-prox" class="sx-input" type="date" value="${proxVal}">
      </div>
      <div class="pl-modal-ft">
        <button class="sx-btn sx-btn-outline sx-btn-sm" id="pl-cotizar-cancel">Cancelar</button>
        <button class="sx-btn sx-btn-primary sx-btn-sm" id="pl-cotizar-go">
          <i class="ph ph-download-simple"></i> Descargar PDF y generar
        </button>
      </div>`;

    if (this._productos.ofrece("vista")) (document.getElementById("cz-vista-tipo") as HTMLSelectElement).value = vistaCfg.tipo;
    if (this._productos.ofrece("protec") && prods.protec.cat) (document.getElementById("cz-protec-cat") as HTMLSelectElement).value = prods.protec.cat;

    const refresh = (): void => {
      const { items, totalNum, hasNegocia } = this._buildCotizItems(fincas, prods, vistaCfg);
      const total = Math.round(totalNum);
      const totalStr = hasNegocia ? (total > 0 ? `Negociación + $${total.toLocaleString("es-CO")}` : "Negociación directa") : total > 0 ? `$${total.toLocaleString("es-CO")}` : "—";
      document.getElementById("cz-live")!.innerHTML = `
        <div class="pl-cotiz-items">
          ${items.length === 0
            ? `<div class="pl-empty" style="padding:8px 0">Selecciona al menos un producto.</div>`
            : items.map(i => `<div class="pl-cotiz-item"><div style="display:flex;align-items:center;gap:6px"><span class="pl-cotiz-tag">${i.tag}</span><span class="pl-cotiz-item-name">${this._esc(i.desc)}</span></div><span class="pl-cotiz-item-price">${i.precio}</span></div>`).join("")}
        </div>
        <div class="pl-cotiz-total"><span>TOTAL COTIZADO</span><span>${totalStr}</span></div>`;
    };

    const el = (id: string): HTMLElement => document.getElementById(id)!;
    (el("cz-dataag") as HTMLInputElement).onchange  = (e) => { prods.dataag = (e.target as HTMLInputElement).checked; refresh(); };
    // Solo existen los campos de lo que la marca vende.
    if (this._productos.ofrece("rastro")) {
      (el("cz-rastro") as HTMLInputElement).oninput   = (e) => { prods.rastro = Math.max(0, Math.floor(Number((e.target as HTMLInputElement).value) || 0)); refresh(); };
    }
    if (this._productos.ofrece("bascula")) {
      (el("cz-bascula") as HTMLInputElement).oninput  = (e) => { prods.bascula = Math.max(0, Math.floor(Number((e.target as HTMLInputElement).value) || 0)); refresh(); };
    }
    if (this._productos.ofrece("vista")) {
      (el("cz-vista") as HTMLInputElement).onchange   = (e) => { prods.vista = (e.target as HTMLInputElement).checked; el("cz-vista-cfg").toggleAttribute("hidden", !prods.vista); refresh(); };
      (el("cz-vista-km") as HTMLInputElement).oninput = (e) => { vistaCfg.km = Math.max(0, Number((e.target as HTMLInputElement).value) || 0); refresh(); };
      (el("cz-vista-tipo") as HTMLSelectElement).onchange = (e) => { vistaCfg.tipo = (e.target as HTMLSelectElement).value; refresh(); };
      (el("cz-vista-arb") as HTMLSelectElement).onchange  = (e) => { vistaCfg.arboles = (e.target as HTMLSelectElement).value; refresh(); };
    }
    if (this._productos.ofrece("protec")) {
      (el("cz-protec") as HTMLInputElement).onchange  = (e) => { prods.protec.activo = (e.target as HTMLInputElement).checked; el("cz-protec-cfg").toggleAttribute("hidden", !prods.protec.activo); refresh(); };
      (el("cz-protec-cat") as HTMLSelectElement).onchange = (e) => { prods.protec.cat = (e.target as HTMLSelectElement).value; refresh(); };
      (el("cz-protec-ha") as HTMLInputElement).oninput = (e) => { prods.protec.ha = Math.max(0, Number((e.target as HTMLInputElement).value) || 0); refresh(); };
    }

    // ── Fincas: lista + alta/borrado que persisten en la BD y recalculan DATA AG (precio por ha) ──
    const renderFincas = (): void => {
      const list = document.getElementById("cz-fincas-list")!;
      list.innerHTML = fincas.length === 0
        ? `<div class="pl-empty" style="padding:6px 0">Sin fincas. Agrega al menos una para cotizar DATA AG por hectáreas.</div>`
        : fincas.map((f, i) => `<div style="display:flex;justify-content:space-between;align-items:center;font-size:13px;padding:5px 8px;background:var(--sx-bg-subtle);border-radius:6px"><span>${this._esc(f.nombre)}${f.municipio ? ` · ${this._esc(f.municipio)}` : ""} · ${f.tipo.startsWith("ganader") ? "Ganadería" : "Agricultura"}</span><span style="display:flex;align-items:center;gap:8px"><span class="pl-cotiz-item-price">${f.ha} ha</span><button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-fi="${i}" title="Eliminar finca"><i class="ph ph-trash"></i></button></span></div>`).join("");
      list.querySelectorAll<HTMLButtonElement>("[data-fi]").forEach(b => {
        b.onclick = () => void delFinca(Number(b.dataset.fi));
      });
      const th = fincas.reduce((s, f) => s + f.ha, 0);
      const sub = document.getElementById("cz-cli-sub");
      if (sub) sub.textContent = `NIT ${nit} · ${fincas.length} finca${fincas.length !== 1 ? "s" : ""} · ${th} ha`;
    };

    // Desvincula la finca de ESTE cliente; la finca sobrevive para sus otros
    // dueños. De ahí el texto, y de ahí que el clienteId sea obligatorio.
    const delFinca = async (i: number): Promise<void> => {
      const f = fincas[i];
      if (!f) return;
      if (!confirm(`¿Eliminar la finca "${f.nombre}"?`)) return;
      try {
        if (f.fincaId) await FincaDeleteService.instance.delete(f.fincaId, op.clienteId);
        fincas.splice(i, 1);
        renderFincas(); refresh();
      } catch (err) {
        errorMessageComponent.addError("Error al eliminar finca", (err as Error).message);
      }
    };

    const addFinca = async (): Promise<void> => {
      const nombreI = (document.getElementById("cz-fnc-nombre") as HTMLInputElement);
      const tipoI   = (document.getElementById("cz-fnc-tipo")   as HTMLSelectElement);
      const haI     = (document.getElementById("cz-fnc-ha")     as HTMLInputElement);
      const deptoI  = (document.getElementById("cz-fnc-depto")  as HTMLSelectElement);
      const muniI   = (document.getElementById("cz-fnc-muni")   as HTMLSelectElement);
      const fNombre = nombreI.value.trim();
      const fTipo   = Number(tipoI.value);              // 0 agricultura · 1 ganadería
      const fHa     = Math.max(0, Number(haI.value) || 0);
      if (!fNombre || fHa <= 0) { errorMessageComponent.addWarning("Datos incompletos", "Ingresa el nombre y las hectáreas de la finca."); return; }
      const muniNombre = muniI.selectedIndex > 0 ? muniI.options[muniI.selectedIndex].text : "";
      const btn = document.getElementById("cz-fnc-add") as HTMLButtonElement;
      btn.disabled = true;
      try {
        const payload: FincaPayload = {
          id: null, clienteId: op.clienteId, nombre: fNombre,
          tipo: fTipo, hectareasFinca: fHa,
          departamentoId: deptoI.value || null, municipioId: muniI.value || null,
        };
        const saved = await FincaSaveService.instance.save(payload);
        fincas.push({ id: fincas.length + 1, fincaId: saved.id, nombre: fNombre, tipo: fTipo === 1 ? "ganadería" : "agricultura", municipio: muniNombre, ha: fHa });
        nombreI.value = ""; haI.value = "";
        deptoI.value = ""; this._onGeoDeptoChange("cz-fnc-depto", "cz-fnc-muni");
        renderFincas(); refresh();
      } catch (err) {
        errorMessageComponent.addError("Error al agregar finca", (err as Error).message);
      } finally { btn.disabled = false; }
    };

    document.getElementById("cz-fnc-add")!.onclick = () => void addFinca();
    void this._wireGeoSelects("cz-fnc-depto", "cz-fnc-muni");
    renderFincas();

    document.getElementById("pl-modal-overlay")!.classList.add("open");
    document.getElementById("pl-cotizar-cancel")!.onclick = () => this._closeModal();
    document.getElementById("pl-cotizar-go")!.onclick     = () => void this._confirmCotizar(op, fincas, prods, vistaCfg);
    refresh();
  }

  /** Confirma la cotización: descarga PDF (con la config editada) + actualiza la op (valorCierre + productos + notas). La op ya está en proposal; la etapa no cambia. */
  private async _confirmCotizar(op: OportunidadResponse, fincas: CotizFinca[], prods: CotizProds, vistaCfg: { km: number; tipo: string; arboles: string }): Promise<void> {
    if (fincas.length === 0) { errorMessageComponent.addWarning("Sin fincas", "El cliente no tiene fincas para cotizar."); return; }
    const { totalNum, hasNegocia } = this._buildCotizItems(fincas, prods, vistaCfg);
    const total = Math.round(totalNum);
    if (total === 0 && !hasNegocia) { errorMessageComponent.addWarning("Sin productos", "Selecciona al menos un producto para generar la cotización."); return; }
    const btn = document.getElementById("pl-cotizar-go") as HTMLButtonElement | null;
    if (btn) btn.disabled = true;

    const cli     = this._cliente(op.clienteId);
    const proxRaw = (document.getElementById("pl-cotizar-prox") as HTMLInputElement)?.value ?? "";
    const notas   = (document.getElementById("cz-notas") as HTMLTextAreaElement)?.value ?? op.notas ?? "";

    try {
      // 1) PDF con la configuración editada (incluye el km real de VISTA)
      this._downloadCotizPdf({
        nombre:   cli?.nombre ?? op.clienteNombre ?? "Cliente",
        nit:      cli?.nit ?? op.clienteNit ?? "",
        whatsapp: cli?.telefono ?? "",
        depto:    "",
        notas,
        fincas, prods, vista: vistaCfg,
      });

      // 2) Actualizar la oportunidad → valorCierre + productos + notas (se mantiene en proposal)
      const save = new OportunidadSavePayload();
      save.id           = op.id;
      save.clienteId    = op.clienteId;
      save.source       = op.source;
      save.stage        = op.stage;
      save.comercialId  = op.comercialId ?? null;
      save.proxContacto = proxRaw ? new Date(proxRaw + "T12:00:00").toISOString() : (op.proxContacto ?? null);
      save.valorCierre  = total > 0 ? total : (op.valorCierre != null ? Number(op.valorCierre) : null);
      save.notas        = notas;
      save.productos    = { ...prods, protec: { ...prods.protec } };
      const updated = await PipelineService.instance.saveOportunidad(save);

      // 3) Evento: cotización enviada
      try {
        const evento = new EventoPayload();
        evento.tipo = "note";
        evento.metadata = { texto: `📄 Cotización enviada — $${total.toLocaleString("es-CO")}` };
        this._replaceOp(await PipelineService.instance.addEvento(updated.id, evento));
      } catch (error) {
        console.warn(`[PipelinePage._confirmCotizar] no se pudo registrar el evento de la cotización en la oportunidad ${updated.id}`, error);
        this._replaceOp(updated);
      }

      this._closeModal();
      errorMessageComponent.addSuccess("Cotización generada", `PDF descargado. Total: $${total.toLocaleString("es-CO")} COP`);
      if (this._currentClienteId === op.clienteId) this._loadFicha();
    } catch (err) {
      errorMessageComponent.addError("Error al generar la cotización", (err as Error).message);
      if (btn) btn.disabled = false;
    }
  }

  /** Construye los ítems de la cotización (para el PDF) a partir de fincas + productos. */
  private _buildCotizItems(fincas: CotizFinca[], prods: CotizProds, vista?: { km: number; tipo: string; arboles: string }): { items: { desc: string; precio: string; tag: string }[]; totalNum: number; hasNegocia: boolean } {
    const totalHa = fincas.reduce((s, f) => s + f.ha, 0);
    const items: { desc: string; precio: string; tag: string }[] = [];
    let totalNum = 0, hasNegocia = false;

    const daLabel   = getServicio("dataag")?.nombre ?? "DATA AG";
    const rastroLabel = getServicio("rastro")?.nombre ?? "RASTRO GPS";
    const bascLabel   = getServicio("bascula")?.nombre ?? "Báscula Conectada";
    const vistaLabel  = getServicio("vista")?.nombre ?? "VISTA";
    const protecLabel = getServicio("protec")?.nombre ?? "PROTEC";

    if (prods.dataag && fincas.length > 0) {
      const v = this._calcDataAGFromFincas(fincas);
      if (v === "negociacion") { hasNegocia = true; items.push({ desc: `${daLabel} — Suscripción anual · ${fincas.length} finca(s) · ${totalHa} ha`, precio: "Negociación directa", tag: "SOFTWARE" }); }
      else if (v) { totalNum += v; items.push({ desc: `${daLabel} — Suscripción anual · ${fincas.length} finca(s) · ${totalHa} ha`, precio: `$${Math.round(v).toLocaleString("es-CO")} COP/año`, tag: "SOFTWARE" }); }
    }
    if (prods.rastro > 0) {
      const v = this._calcRastro(prods.rastro); totalNum += v;
      items.push({ desc: `${rastroLabel} — ${prods.rastro} unidad(es)`, precio: `$${Math.round(v).toLocaleString("es-CO")} COP (c/IVA)`, tag: "EQUIPO" });
    }
    if (prods.bascula > 0) {
      const v = this._calcBascula(prods.bascula); totalNum += v;
      items.push({ desc: `${bascLabel} — ${prods.bascula} unidad(es)`, precio: `$${Math.round(v).toLocaleString("es-CO")} COP (c/IVA)`, tag: "EQUIPO" });
    }
    if (prods.vista) {
      const km  = vista?.km ?? 0;
      const tv  = vista?.tipo || (fincas.filter(f => f.tipo.startsWith("ganader")).reduce((s, f) => s + f.ha, 0) > totalHa / 2 ? "ganadería" : "agricultura");
      const arb = vista?.arboles ?? "no";
      const vr  = this._calcVISTA(km, totalHa, tv, arb);
      if (vr) {
        totalNum += vr.total;
        items.push({
          desc: `${vistaLabel} — Fotogrametría${arb === "si" ? " + Conteo de árboles" : ""}`,
          precio: `$${Math.round(vr.total).toLocaleString("es-CO")} COP / visita`, tag: "SERVICIO",
        });
      }
    }
    if (prods.protec.activo) {
      const pr = this._calcPROTEC(prods.protec.cat, prods.protec.ha);
      if (pr) {
        totalNum += pr.total;
        items.push({ desc: `${protecLabel} — ${pr.label}`, precio: `$${pr.total.toLocaleString("es-CO")} COP / aplicación`, tag: "SERVICIO" });
      } else if (prods.protec.ha > 0 && prods.protec.ha < 10) {
        items.push({ desc: `${protecLabel} — Mínimo 10 ha requerido`, precio: "No incluido", tag: "SERVICIO" });
      }
    }
    return { items, totalNum, hasNegocia };
  }

  /** Descarga un PDF de cotización desde una oportunidad de la ficha (usa las fincas del cliente). */
  private async _downloadOpPdf(op: OportunidadResponse): Promise<void> {
    const cliRec = this._cliente(op.clienteId);
    let fincas: CotizFinca[] = [];
    try {
      const raw = await FincaListByClienteIdService.instance(op.clienteId).get();
      fincas = raw.filter(f => !f.isEliminado).map(f => ({
        id: 0,
        nombre: f.nombreFinca,
        tipo: f.isGanaderia ? "ganadería" : (f.tipo?.toLowerCase() ?? "agricultura"),
        municipio: f.municipioNombre ?? "",
        ha: f.hectareasFinca,
      }));
    } catch (error) {
      console.warn(`[PipelinePage._downloadOpPdf] no se pudieron cargar las fincas del cliente ${op.clienteId}`, error);
      errorMessageComponent.addError("Error al cargar las fincas", (error as Error).message);
    }

    const prods: CotizProds = op.productos
      ? this._productos.filtrar(op.productos)
      : this._defaultCotizProds();

    this._downloadCotizPdf({
      nombre:   cliRec?.nombre ?? op.clienteNombre,
      nit:      cliRec?.nit ?? op.clienteNit ?? "",
      whatsapp: cliRec?.telefono ?? "",
      depto:    "",
      notas:    op.notas,
      fincas,
      prods,
    });
  }

  /** Genera y descarga el PDF de la cotización (layout A4 fiel al formato Sextante). */
  private _downloadCotizPdf(data: {
    nombre: string; nit: string; whatsapp: string; depto: string; notas: string;
    fincas: CotizFinca[]; prods: CotizProds; vista?: { km: number; tipo: string; arboles: string };
  }): void {
    const cli = { nombre: data.nombre, nit: data.nit, whatsapp: data.whatsapp, depto: data.depto, notas: data.notas };
    const fincas = data.fincas;
    const totalHa = fincas.reduce((s, f) => s + f.ha, 0);
    const { items, totalNum, hasNegocia } = this._buildCotizItems(fincas, data.prods, data.vista);

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, H = 297, M = 18;

    // Header — navy de marca (--sx-navy-600 #0a3246)
    doc.setFillColor(10, 50, 70); doc.rect(0, 0, W, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18); doc.setFont("helvetica", "bold"); doc.text("SEXTANTE", M, 17);
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(160, 185, 220);
    doc.text("TECNOLOGÍA AGRÍCOLA DE PRECISIÓN", M, 23);
    doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
    doc.text("COTIZACIÓN", W - M, 14, { align: "right" });
    const dateStr  = new Date().toLocaleDateString("es-CO", { day: "2-digit", month: "long", year: "numeric" });
    const cotizNum = "SXT-" + String(Date.now()).slice(-6);
    doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor(160, 185, 220);
    doc.text(dateStr, W - M, 20, { align: "right" });
    doc.text(cotizNum, W - M, 25, { align: "right" });

    doc.setFillColor(122, 201, 67); doc.rect(0, 28, W, 3, "F");
    let y = 38;

    // Cliente
    doc.setFillColor(248, 246, 239); doc.roundedRect(M, y, W - M * 2, 28, 3, 3, "F");
    doc.setTextColor(155, 153, 145); doc.setFontSize(7); doc.setFont("helvetica", "bold");
    doc.text("DATOS DEL CLIENTE", M + 6, y + 6);
    doc.setTextColor(30, 29, 26); doc.setFontSize(13); doc.setFont("helvetica", "bold");
    doc.text(cli.nombre, M + 6, y + 14);
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(92, 91, 86);
    const meta = [cli.nit, cli.whatsapp, cli.depto, `${fincas.length} finca(s) · ${totalHa} ha totales`].filter(Boolean).join("  ·  ");
    doc.text(meta, M + 6, y + 21, { maxWidth: W - M * 2 - 12 });
    y += 35;

    // Fincas
    doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(155, 153, 145);
    doc.text("DETALLE DE FINCAS / PREDIOS", M, y); y += 5;
    fincas.forEach((f, i) => {
      const even = i % 2 === 0;
      doc.setFillColor(even ? 248 : 255, even ? 246 : 255, even ? 239 : 255);
      doc.rect(M, y, W - M * 2, 8, "F");
      doc.setTextColor(30, 29, 26); doc.setFontSize(8); doc.setFont("helvetica", "bold");
      doc.text(f.nombre, M + 4, y + 5.5);
      doc.setFont("helvetica", "normal"); doc.setTextColor(92, 91, 86);
      doc.text(`${f.tipo.charAt(0).toUpperCase() + f.tipo.slice(1)} · ${f.ha} ha · ${f.municipio || "—"}`, M + 70, y + 5.5);
      y += 8;
    });
    y += 6;

    // Productos
    doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(155, 153, 145);
    doc.text("SERVICIOS Y PRODUCTOS COTIZADOS", M, y); y += 5;
    doc.setFillColor(16, 26, 50); doc.rect(M, y, W - M * 2, 8, "F");
    doc.setTextColor(255, 255, 255); doc.setFontSize(7); doc.setFont("helvetica", "bold");
    doc.text("Descripción", M + 4, y + 5.5);
    doc.text("Precio", W - M - 4, y + 5.5, { align: "right" });
    y += 8;

    const tagColors: Record<string, [number, number, number]> = { SOFTWARE: [0, 149, 226], EQUIPO: [139, 92, 246], SERVICIO: [46, 176, 84] };
    items.forEach((item, i) => {
      const rowH = 9;
      const [br, bg, bb] = i % 2 === 0 ? [255, 255, 255] : [248, 246, 239];
      doc.setFillColor(br, bg, bb); doc.rect(M, y, W - M * 2, rowH, "F");
      const [tr, tg, tb] = tagColors[item.tag] ?? [155, 153, 145];
      doc.setFillColor(tr, tg, tb); doc.roundedRect(M + 3, y + 2.5, 18, 4.5, 1, 1, "F");
      doc.setFontSize(6); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
      doc.text(item.tag, M + 12, y + 5.5, { align: "center" });
      doc.setTextColor(30, 29, 26); doc.setFontSize(8); doc.setFont("helvetica", "normal");
      doc.text(item.desc, M + 24, y + 6);
      doc.setFont("helvetica", "bold"); doc.setTextColor(26, 122, 58); doc.setFontSize(8);
      doc.text(item.precio, W - M - 4, y + 6, { align: "right" });
      y += rowH;
    });
    if (!items.length) {
      doc.setFillColor(255, 255, 255); doc.rect(M, y, W - M * 2, 9, "F");
      doc.setTextColor(155, 153, 145); doc.setFontSize(8);
      doc.text("Sin productos seleccionados", M + 4, y + 6); y += 9;
    }

    // Total
    doc.setFillColor(16, 26, 50); doc.rect(M, y, W - M * 2, 11, "F");
    doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont("helvetica", "bold");
    doc.text(data.prods.protec.activo && !data.prods.dataag && data.prods.rastro === 0 && data.prods.bascula === 0 && !data.prods.vista ? "TOTAL POR APLICACIÓN" : "TOTAL ESTIMADO", M + 4, y + 7.5);
    const totalStr = hasNegocia ? (totalNum > 0 ? `Negociación + $${Math.round(totalNum).toLocaleString("es-CO")} COP` : "Ver detalle") : totalNum > 0 ? `$${Math.round(totalNum).toLocaleString("es-CO")} COP` : "—";
    doc.text(totalStr, W - M - 4, y + 7.5, { align: "right" });
    y += 18;

    // Condiciones
    doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(155, 153, 145);
    doc.text("CONDICIONES COMERCIALES", M, y); y += 5;
    // Solo las condiciones de lo que la marca vende. Los precios de RASTRO y Báscula salen de las mismas tarifas con que
    // se calcula el total (hasta el 2026-10-25 estaban escritos en el texto).
    const pesos = (n: number): string => `$${Math.round(n).toLocaleString("es-CO")}`;
    const tramosRastro = this._tramosRastro()
      .map(t => `${t.hasta === null ? `${t.desde}+` : `${t.desde}–${t.hasta}`}: ${pesos(t.precio)}`)
      .join(" | ");
    const condiciones = [
      "• DATA AG: suscripción anual prepago sobre total de hectáreas del cliente. Incluye onboarding, capacitación y soporte.",
      this._productos.ofrece("rastro") ? `• RASTRO: precio por unidad (${tramosRastro}) + IVA 19%.` : null,
      this._productos.ofrece("bascula") ? `• Báscula Conectada: ${pesos(this._precioBascula())} por unidad + IVA 19%. Instalación coordinada con equipo técnico.` : null,
      this._productos.ofrece("vista") ? "• VISTA (Fotogrametría): precio por visita. Incluye desplazamiento, vuelo y procesamiento de imágenes." : null,
      this._productos.ofrece("protec") ? "• PROTEC (Aspersión): precio por aplicación/ha. Mínimo 10 ha por servicio." : null,
      "• Cotización válida por 30 días calendario desde la fecha de emisión.",
    ].filter((c): c is string => c !== null);
    doc.setFont("helvetica", "normal"); doc.setTextColor(92, 91, 86); doc.setFontSize(7.5);
    condiciones.forEach(c => { doc.text(c, M, y, { maxWidth: W - M * 2 }); y += 5; });
    y += 4;

    // Notas
    if (cli.notas) {
      doc.setFillColor(255, 244, 224); doc.roundedRect(M, y, W - M * 2, 18, 2, 2, "F");
      doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(133, 79, 11);
      doc.text("NOTAS DE LA VISITA", M + 4, y + 6);
      doc.setFont("helvetica", "normal"); doc.setTextColor(99, 56, 6); doc.setFontSize(8);
      doc.text(cli.notas, M + 4, y + 12, { maxWidth: W - M * 2 - 8 });
    }

    // Footer
    doc.setFillColor(248, 246, 239); doc.rect(0, H - 22, W, 22, "F");
    doc.setDrawColor(229, 227, 218); doc.line(0, H - 22, W, H - 22);
    doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor(155, 153, 145);
    doc.text("Sextante Drones S.A.S.  ·  www.sextantedrones.com  ·  ventas@sextantedrones.com", W / 2, H - 14, { align: "center" });
    doc.text(`Cotización ${cotizNum}  ·  ${dateStr}`, W / 2, H - 8, { align: "center" });

    doc.save(`Cotizacion_Sextante_${cli.nombre.replace(/\s+/g, "_")}_${cotizNum}.pdf`);
  }

  // ── Modal nueva oportunidad (2 pasos) ──────────────────────────────────────

  private _openModal(): void {
    this._modalStep    = "search";
    this._modalCliente = null;
    this._modalSource  = "web";
    this._modalPrefill = "";
    this._modalEditOp  = null;
    this._renderModalStep();
    document.getElementById("pl-modal-overlay")!.classList.add("open");
  }

  private _openModalForCliente(c: ClienteSearchItem): void {
    this._modalStep    = "existing";
    this._modalCliente = c;
    this._modalSource  = "web";
    this._modalEditOp  = null;
    this._renderModalStep();
    document.getElementById("pl-modal-overlay")!.classList.add("open");
  }

  /**
   * Modo edición: reusa el formulario de creación sobre una op existente.
   * `POST /save` con `id` ya actualiza source/comercial/proxContacto/notas/productos —
   * no hacía falta endpoint nuevo, solo faltaba la pantalla.
   */
  private _openEditOpModal(op: OportunidadResponse): void {
    this._modalStep   = "edit";
    this._modalEditOp = op;
    this._modalSource = op.source;
    this._renderModalStep();
    document.getElementById("pl-modal-overlay")!.classList.add("open");
  }

  private _closeModal(): void {
    document.getElementById("pl-modal-overlay")!.classList.remove("open");
  }

  /**
   * Pinta el paso actual de la ventana de oportunidad (`_modalStep`) y conecta sus controles:
   * - «search»: busca el cliente en el navegador al escribir (`_modalSearch`, sobre la lista que la página cargó al
   *   abrirse; desde el 2026-10-28, sin espera ni RPC) o pasa a crear uno nuevo;
   * - «existing»: muestra la tarjeta del cliente escogido y pide los datos de la oportunidad nueva;
   * - «new»: pide los datos del cliente nuevo, los de su finca inicial (cultivo, hectáreas, departamento y municipio) y
   *   los de su oportunidad;
   * - «edit»: pide los datos de la oportunidad que se edita.
   *
   * Los datos de la oportunidad salen de `_modalOpFieldsHTML` en los tres últimos.
   */
  private _renderModalStep(): void {
    const title   = document.getElementById("pl-modal-title")!;
    const content = document.getElementById("pl-modal-content")!;

    if (this._modalStep === "search") {
      title.textContent = "Buscar cliente";
      content.innerHTML = `
        <div class="pl-modal-body">
          <div class="pl-typeahead">
            <input id="pl-modal-search" class="sx-input" placeholder="Buscar por nombre o NIT…" autocomplete="off" autofocus>
            <div class="pl-typeahead-results" id="pl-modal-results"></div>
          </div>
          <button class="sx-btn sx-btn-outline" style="width:100%;margin-top:12px" id="pl-modal-nuevo-cli">
            <i class="ph ph-plus"></i> Crear nuevo cliente
          </button>
        </div>`;
      const search = document.getElementById("pl-modal-search") as HTMLInputElement;
      search.addEventListener("input", () => this._modalSearch(search.value));
      document.getElementById("pl-modal-nuevo-cli")!.onclick = () => {
        this._modalPrefill = (document.getElementById("pl-modal-search") as HTMLInputElement)?.value.trim() ?? "";
        this._modalStep = "new";
        this._renderModalStep();
      };
      search.focus();
      return;
    }

    if (this._modalStep === "existing") {
      const c = this._modalCliente!;
      title.textContent = "Nueva oportunidad";
      content.innerHTML = `
        <div class="pl-modal-body">
          <div class="pl-cotiz-cli-card" style="margin-bottom:12px">
            <div>
              <div class="pl-cotiz-cli-name">${this._esc(c.nombre)}</div>
              <div class="pl-cotiz-cli-sub">NIT ${this._esc(c.nit)} · ${this._esc(c.telefono || "sin teléfono")} · ${c.hectareasTotales} ha</div>
            </div>
          </div>
          ${this._modalOpFieldsHTML()}
        </div>
        <div class="pl-modal-ft">
          <button class="sx-btn sx-btn-outline sx-btn-sm" id="pl-modal-back">← Volver</button>
          <button class="sx-btn sx-btn-primary sx-btn-sm" id="pl-modal-create">Crear oportunidad</button>
        </div>`;
      this._wireModalOpFields();
      document.getElementById("pl-modal-back")!.onclick   = () => { this._modalStep = "search"; this._renderModalStep(); };
      document.getElementById("pl-modal-create")!.onclick = () => void this._createOportunidadExisting();
      return;
    }

    if (this._modalStep === "edit") {
      const op  = this._modalEditOp!;
      const cli = this._cliente(op.clienteId);
      title.textContent = "Editar oportunidad";
      content.innerHTML = `
        <div class="pl-modal-body">
          <div class="pl-cotiz-cli-card" style="margin-bottom:12px">
            <div>
              <div class="pl-cotiz-cli-name">${this._esc(cli?.nombre ?? op.clienteNombre)}</div>
              <div class="pl-cotiz-cli-sub">NIT ${this._esc(cli?.nit ?? op.clienteNit ?? "—")} · ${getStageLabel(op.stage)}</div>
            </div>
          </div>
          ${this._modalOpFieldsHTML()}
        </div>
        <div class="pl-modal-ft">
          <button class="sx-btn sx-btn-outline sx-btn-sm" id="pl-modal-cancel">Cancelar</button>
          <button class="sx-btn sx-btn-primary sx-btn-sm" id="pl-modal-save">Guardar cambios</button>
        </div>`;
      this._wireModalOpFields();
      this._applyOpToModalFields(op);
      document.getElementById("pl-modal-cancel")!.onclick = () => this._closeModal();
      document.getElementById("pl-modal-save")!.onclick   = () => void this._saveOpEdit();
      return;
    }

    // new
    title.textContent = "Nuevo cliente + oportunidad";
    content.innerHTML = `
      <div class="pl-modal-body">
        <div class="pl-form-section-lbl">Datos del cliente (mínimos)</div>
        <div>
          <label class="pl-label">NIT o cédula <span class="pl-req">*</span></label>
          <input id="pl-nc-nit" class="sx-input" placeholder="900123456 o 12345678">
        </div>
        <div class="pl-modal-row2">
          <div>
            <label class="pl-label">Nombre / razón social <span class="pl-req">*</span></label>
            <input id="pl-nc-nombre" class="sx-input" placeholder="Juan López">
          </div>
          <div>
            <label class="pl-label">Teléfono / WhatsApp</label>
            <input id="pl-nc-tel" class="sx-input" placeholder="+57 300…">
          </div>
        </div>
        <div>
          <label class="pl-label">Correo electrónico</label>
          <input id="pl-nc-email" class="sx-input" type="email" placeholder="correo@empresa.com (opcional)">
        </div>
        <div class="pl-modal-row2">
          <div>
            <label class="pl-label">Cultivo principal</label>
            <select id="pl-nc-cultivo" class="sx-input">
              <option value="">—</option>
              ${["café","aguacate","banano","ganadería","arroz","maíz","caña","cítricos","cacao","otro"].map(t => `<option value="${t}">${t}</option>`).join("")}
            </select>
          </div>
          <div>
            <label class="pl-label">Hectáreas</label>
            <input id="pl-nc-ha" class="sx-input" type="number" placeholder="0">
          </div>
        </div>
        <div class="pl-modal-row2">
          <div>
            <label class="pl-label">Departamento</label>
            <select id="pl-nc-depto" class="sx-input"><option value="">Cargando…</option></select>
          </div>
          <div>
            <label class="pl-label">Municipio</label>
            <select id="pl-nc-muni" class="sx-input" disabled><option value="">— Selecciona departamento —</option></select>
          </div>
        </div>
        <hr class="pl-divider">
        ${this._modalOpFieldsHTML()}
      </div>
      <div class="pl-modal-ft">
        <button class="sx-btn sx-btn-outline sx-btn-sm" id="pl-modal-back">← Volver</button>
        <button class="sx-btn sx-btn-primary sx-btn-sm" id="pl-modal-create">Guardar cliente y crear oportunidad</button>
      </div>`;
    this._wireModalOpFields();
    void this._wireGeoSelects("pl-nc-depto", "pl-nc-muni");
    // Precargar lo buscado: si parece NIT/cédula (empieza con dígito) → NIT; si no → nombre.
    if (this._modalPrefill) {
      const esNit = /^\d/.test(this._modalPrefill.replace(/[.\-\s]/g, ""));
      const target = document.getElementById(esNit ? "pl-nc-nit" : "pl-nc-nombre") as HTMLInputElement | null;
      if (target) target.value = this._modalPrefill;
    }
    document.getElementById("pl-modal-back")!.onclick   = () => { this._modalStep = "search"; this._renderModalStep(); };
    document.getElementById("pl-modal-create")!.onclick = () => void this._createOportunidadNew();
  }

  /**
   * Campos comunes de oportunidad (source, comercial, prox contacto, notas).
   * El comercial asignado es siempre un select de promotores (su id se envía como comercialId).
   */
  private _modalOpFieldsHTML(): string {
    const comercialField = `<select id="pl-modal-comercial" class="sx-input">${this._promotorOptions(null)}</select>`;
    return `
      <div class="pl-modal-sources" id="pl-modal-sources">
        ${["web","whatsapp","app","campo","manual"].map(s => `
          <button class="pl-source-btn ${s === this._modalSource ? "active" : ""}" data-src="${s}">${SOURCE_LABELS[s]}</button>`).join("")}
      </div>
      <div class="pl-hint" id="pl-source-hint">→ Entrará en: ${SOURCE_STAGE_HINTS[this._modalSource] ?? "Lead nuevo"}</div>
      <div class="pl-modal-row2">
        <div>
          <label class="pl-label">Comercial asignado</label>
          ${comercialField}
        </div>
        <div>
          <label class="pl-label">Próximo contacto</label>
          <input id="pl-modal-prox" class="sx-input" type="date">
        </div>
      </div>
      <div>
        <label class="pl-label" style="margin-bottom:6px;display:block">Productos de interés</label>
        <div class="pl-modal-prods" id="pl-modal-prods">
          ${getServicios().filter(s => this._productos.ofrece(s.id)).map(s => this._modalProdCardHTML(s)).join("")}
        </div>
      </div>
      <div>
        <label class="pl-label">Notas</label>
        <textarea id="pl-modal-notas" class="sx-input" rows="2" placeholder="Observaciones iniciales…" style="resize:vertical"></textarea>
      </div>`;
  }

  private _modalProdCardHTML(s: ServicioItemDto): string {
    const prefix = `pl-mprod-${s.id}`;
    const checked = s.id === "dataag";
    const fieldsHTML = this._modalProdFieldsHTML(s, prefix);
    return `<div class="pl-mprod${checked ? " active" : ""}" id="${prefix}" data-servicio-id="${s.id}">
      <div class="pl-mprod-hd">
        <span class="pl-mprod-name">${s.nombre}</span>
        <span class="pl-mprod-chk">${checked ? "✓" : "+"}</span>
      </div>
      ${fieldsHTML}
    </div>`;
  }

  private _modalProdFieldsHTML(s: ServicioItemDto, prefix: string): string {
    const campos = s.campos;
    if (!s.configurable || !campos?.length) return "";
    const display = s.id === "dataag" ? "block" : "none";
    let inner = "";
    for (const campo of campos) {
      if (campo.tipo === "select" && campo.fuente === "precios.protec") {
        const opts = this._buildProtecOptions();
        inner += `<select id="${prefix}-${campo.clave}" class="sx-input" style="margin-bottom:4px;"><option value="">${campo.label}</option>${opts}</select>`;
      } else if (campo.tipo === "select") {
        const opts = campo.opciones?.map(o => `<option value="${o}">${o}</option>`).join("") ?? "";
        inner += `<select id="${prefix}-${campo.clave}" class="sx-input" style="margin-bottom:4px;"><option value="">${campo.label}</option>${opts}</select>`;
      } else if (campo.tipo === "numero") {
        inner += `<input id="${prefix}-${campo.clave}" class="sx-input" type="number" placeholder="${campo.label}" ${campo.min != null ? `min="${campo.min}"` : ""}${s.id !== "dataag" ? ` value="${campo.min ?? ""}"` : ""}>`;
      }
    }
    return `<div class="pl-mprod-fields" style="display:${display}">${inner}</div>`;
  }

  /**
   * Estado visual de una tarjeta de producto del modal: clase, check y campos configurables.
   * Compartido por el toggle manual y por la precarga en modo edición.
   */
  private _setProdCardActive(card: HTMLElement, on: boolean): void {
    card.classList.toggle("active", on);
    const chk = card.querySelector(".pl-mprod-chk");
    if (chk) chk.textContent = on ? "✓" : "+";
    const fields = card.querySelector(".pl-mprod-fields") as HTMLElement | null;
    if (fields) fields.style.display = on ? "block" : "none";
  }

  /**
   * Texto bajo los botones de fuente. Al crear anuncia la etapa de entrada; al editar eso
   * sería falso (la etapa no se toca aquí, se cambia con su propio selector en la ficha).
   */
  private _sourceHintText(): string {
    if (this._modalStep === "edit") {
      return `Etapa actual: ${getStageLabel(this._modalEditOp?.stage ?? "new")} · editar no la cambia`;
    }
    return `→ Entrará en: ${SOURCE_STAGE_HINTS[this._modalSource] ?? "Lead nuevo"}`;
  }

  private _wireModalOpFields(): void {
    document.querySelectorAll<HTMLElement>("#pl-modal-sources .pl-source-btn").forEach(btn => {
      btn.onclick = () => {
        this._modalSource = btn.dataset.src!;
        document.querySelectorAll<HTMLElement>("#pl-modal-sources .pl-source-btn").forEach(b => b.classList.toggle("active", b.dataset.src === this._modalSource));
        const hint = document.getElementById("pl-source-hint");
        if (hint) hint.textContent = this._sourceHintText();
      };
    });

    const prods = document.getElementById("pl-modal-prods");
    if (prods) {
      prods.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        // Clicks sobre los campos configurables (select/input de VISTA, PROTEC, etc.) no togglean la card
        if (target.closest(".pl-mprod-fields")) return;
        const card = target.closest("[data-servicio-id]") as HTMLElement | null;
        if (!card) return;
        this._setProdCardActive(card, !card.classList.contains("active"));
      });
    }
  }

  /**
   * Precarga el formulario del modal (ya renderizado) con los valores de una op existente.
   * Se hace sobre el DOM en vez de parametrizar los constructores de HTML para no tocar el
   * flujo de creación, que comparte exactamente el mismo markup.
   */
  private _applyOpToModalFields(op: OportunidadResponse): void {
    document.querySelectorAll<HTMLElement>("#pl-modal-sources .pl-source-btn")
      .forEach(b => b.classList.toggle("active", b.dataset.src === op.source));
    const hint = document.getElementById("pl-source-hint");
    if (hint) hint.textContent = this._sourceHintText();

    const com = document.getElementById("pl-modal-comercial") as HTMLSelectElement | null;
    if (com) {
      com.value = op.comercialId ?? "";
      // El comercial de la op puede ser un promotor inactivo, que `_loadData` filtra de
      // `_promotores`: sin su <option> el select cae en "Sin asignar" y al guardar se
      // perdería la asignación sin que el usuario lo note.
      if (op.comercialId && com.value !== op.comercialId) {
        com.insertAdjacentHTML("beforeend", `<option value="${op.comercialId}">${this._esc(op.comercialNombre ?? "Comercial actual")}</option>`);
        com.value = op.comercialId;
      }
    }
    const prox = document.getElementById("pl-modal-prox") as HTMLInputElement | null;
    if (prox) prox.value = op.proxContacto ? op.proxContacto.slice(0, 10) : "";
    const notas = document.getElementById("pl-modal-notas") as HTMLTextAreaElement | null;
    if (notas) notas.value = op.notas ?? "";

    // `productos` puede ser null (ops de landing). La tarjeta de DATA AG nace activa en el
    // HTML de creación, así que hay que apagarla explícitamente si la op no la lleva.
    const p = op.productos;
    const set = (id: string, on: boolean): void => {
      const card = document.getElementById(`pl-mprod-${id}`);
      if (card) this._setProdCardActive(card, on);
    };
    const field = (id: string, campo: string, v: string): void => {
      const el = document.getElementById(`pl-mprod-${id}-${campo}`) as HTMLInputElement | HTMLSelectElement | null;
      if (el) el.value = v;
    };

    set("dataag",  !!p?.dataag);
    set("rastro",  (p?.rastro ?? 0) > 0);
    set("bascula", (p?.bascula ?? 0) > 0);
    set("vista",   !!p?.vista);
    set("protec",  !!p?.protec?.activo);

    if ((p?.rastro ?? 0) > 0)  field("rastro",  "unidades", String(p!.rastro));
    if ((p?.bascula ?? 0) > 0) field("bascula", "unidades", String(p!.bascula));
    if (p?.protec?.activo) {
      field("protec", "categoria", p.protec.cat ?? "");
      field("protec", "hectareas", String(p.protec.ha ?? 0));
    }
  }

  /**
   * Cablea selects dependientes Departamento→Municipio.
   * Reutilizable: paso 2b del modal y alta de finca en la cotización.
   */
  private async _wireGeoSelects(deptoElId: string, muniElId: string): Promise<void> {
    const depto = document.getElementById(deptoElId) as HTMLSelectElement | null;
    const muni  = document.getElementById(muniElId)  as HTMLSelectElement | null;
    if (!depto || !muni) return;
    depto.addEventListener("change", () => this._onGeoDeptoChange(deptoElId, muniElId));
    try {
      const [deptos, munis] = await Promise.all([
        DepartamentoListService.instance.get(),
        MunicipioListService.instance.get(),
      ]);
      this._geoMunis = munis;
      depto.innerHTML = `<option value="">— Departamento —</option>` +
        deptos.map(d => `<option value="${d.id}">${this._esc(d.nombre)}</option>`).join("");
    } catch (error) {
      console.warn("[PipelinePage._wireGeoSelects] no se pudieron cargar los departamentos y municipios", error);
      depto.innerHTML = `<option value="">Error al cargar</option>`;
    }
  }

  private _onGeoDeptoChange(deptoElId: string, muniElId: string): void {
    const depto = document.getElementById(deptoElId) as HTMLSelectElement | null;
    const muni  = document.getElementById(muniElId)  as HTMLSelectElement | null;
    if (!depto || !muni) return;
    const list = depto.value ? this._geoMunis.filter(m => m.departamentoId === depto.value) : [];
    muni.innerHTML = `<option value="">— Municipio —</option>` +
      list.map(m => `<option value="${m.id}">${this._esc(m.nombre)}</option>`).join("");
    muni.disabled = !depto.value;
  }

  /**
   * Buscador de clientes del modal de nueva oportunidad: busca en la lista de clientes que la página cargó al abrirse
   * (`buscarClientes`, en el navegador: al momento, sin mínimo de letras) y muestra hasta 10. Al elegir uno pasa al
   * paso del cliente existente.
   */
  private _modalSearch(q: string): void {
    const results = document.getElementById("pl-modal-results");
    if (!results) return;
    if (q.trim() === "") { results.innerHTML = ""; return; }
    const items = buscarClientes(this._clientes, q, 10);
    results.innerHTML = items.length === 0
      ? `<div class="pl-typeahead-empty">Sin coincidencias</div>`
      : items.map(c => `
        <div class="pl-typeahead-item" data-id="${c.id}">
          <div class="pl-typeahead-name">${this._esc(c.nombre)}</div>
          <div class="pl-typeahead-sub">NIT ${this._esc(c.nit)} · ${this._esc(c.telefono || "—")} · ${c.hectareasTotales} ha</div>
        </div>`).join("");
    results.querySelectorAll<HTMLElement>(".pl-typeahead-item").forEach(item => {
      const c = items.find(x => x.id === item.dataset.id)!;
      item.onclick = () => { this._modalCliente = c; this._modalStep = "existing"; this._renderModalStep(); };
    });
  }

  /**
   * Guarda la edición de una op existente. Reusa `_buildOpPayload` y sobreescribe lo que el
   * formulario no debe tocar:
   *  - `stage`: se cambia con `POST /{id}/stage`, que además registra el evento `stage_change`.
   *  - `valorCierre`: lo fija la cotización; se reenvía para que editar no lo borre.
   */
  private async _saveOpEdit(): Promise<void> {
    const op = this._modalEditOp;
    if (!op) return;
    const btn = document.getElementById("pl-modal-save") as HTMLButtonElement;
    btn.disabled = true;
    try {
      const payload = this._buildOpPayload(op.clienteId);
      payload.id          = op.id;
      payload.stage       = op.stage;
      payload.valorCierre = op.valorCierre != null ? Number(op.valorCierre) : null;

      this._replaceOp(await PipelineService.instance.saveOportunidad(payload));
      this._closeModal();
      errorMessageComponent.addSuccess("Oportunidad actualizada", "Los cambios se guardaron.");
      if (this._currentClienteId === op.clienteId) this._loadFicha();
    } catch (err) {
      errorMessageComponent.addError("Error al guardar los cambios", (err as Error).message);
      btn.disabled = false;
    }
  }

  private async _createOportunidadExisting(): Promise<void> {
    const c = this._modalCliente;
    if (!c) return;
    const btn = document.getElementById("pl-modal-create") as HTMLButtonElement;
    btn.disabled = true;
    try {
      const nueva = await PipelineService.instance.saveOportunidad(this._buildOpPayload(c.id));
      this._ops = [nueva, ...this._ops];
      this._updateCount();
      this._renderKanban();
      this._renderLista();
      this._closeModal();
      this._openFicha(c.id, nueva.id);
    } catch (err) {
      errorMessageComponent.addError("Error al crear oportunidad", (err as Error).message);
      btn.disabled = false;
    }
  }

  /**
   * Crea desde el modal un cliente nuevo, su finca inicial (si se cargaron hectáreas, cultivo o municipio) y la
   * oportunidad. Antes revisa que no exista otro cliente con el mismo NIT. Si la finca no se puede guardar, el cliente y
   * la oportunidad se crean igual y se avisa para cargarla desde la ficha.
   */
  private async _createOportunidadNew(): Promise<void> {
    const nombre  = (document.getElementById("pl-nc-nombre") as HTMLInputElement).value.trim();
    const nit     = (document.getElementById("pl-nc-nit") as HTMLInputElement).value.trim();
    const tel     = (document.getElementById("pl-nc-tel") as HTMLInputElement).value.trim();
    const email   = (document.getElementById("pl-nc-email") as HTMLInputElement).value.trim();
    const cultivo    = (document.getElementById("pl-nc-cultivo") as HTMLSelectElement).value;
    const ha         = parseInt((document.getElementById("pl-nc-ha") as HTMLInputElement).value) || 0;
    const deptoId    = (document.getElementById("pl-nc-depto") as HTMLSelectElement).value;
    const muniSel    = document.getElementById("pl-nc-muni") as HTMLSelectElement;
    const muniId     = muniSel.value;
    const muniNombre = muniSel.selectedOptions[0]?.textContent?.trim() ?? "";
    if (!nombre || !nit) { alert("Nombre y NIT/cédula son obligatorios."); return; }

    const btn = document.getElementById("pl-modal-create") as HTMLButtonElement;
    btn.disabled = true;
    try {
      // Dedup por NIT, contra la lista de clientes pedida de nuevo: así cuenta también un cliente que otro usuario creó
      // después de abrir la página. Si no se puede actualizar, `get()` avisa y se revisa contra la que ya estaba.
      this._clientes = await ClienteService.instance.get();
      const dup = this._clientes.find(c => c.nit === nit);
      if (dup) {
        alert(`Ya existe un cliente con ese NIT: "${dup.nombre}". Usa la búsqueda para encontrarlo.`);
        this._modalCliente = dup;
        this._modalStep = "existing";
        this._renderModalStep();
        return;
      }

      // El promotor seleccionado ("Comercial asignado") es un id de promotor válido:
      // queda como comercialId de la oportunidad y como promotorId del cliente.
      const promotorId = this._resolveComercialId();
      const cliPayload: ClientePayload = { id: null, nit, razonSocial: nombre, email, telefono: tel, promotorId };
      const nuevoCli = await new ClienteSaveService().save(cliPayload);
      this._clientes = [nuevoCli, ...this._clientes];

      // Finca inicial: tipo = 0 Agricultura / 1 Ganadería (derivado del cultivo);
      // departamento y municipio con ids reales de catálogo.
      if (ha > 0 || cultivo || muniId) {
        const fincaPayload: FincaPayload = {
          id: null,
          clienteId: nuevoCli.id,
          nombre: muniId ? `Finca en ${muniNombre}` : "Finca principal",
          tipo: cultivo === "ganadería" ? 1 : (cultivo ? 0 : null),
          hectareasFinca: ha > 0 ? ha : null,
          departamentoId: deptoId || null,
          municipioId: muniId || null,
        };
        // Si falla, el cliente y la oportunidad se crean igual; se avisa para cargar la finca desde la ficha.
        await FincaSaveService.instance.save(fincaPayload).catch((error) => {
          console.warn(`[PipelinePage._createOportunidadNew] no se pudo guardar la finca inicial del cliente ${nuevoCli.id}`, error);
          errorMessageComponent.addWarning("Finca sin guardar",
            "El cliente se creó, pero no se pudo guardar su finca: agrégala desde la ficha del cliente.");
        });
      }

      const nueva = await PipelineService.instance.saveOportunidad(this._buildOpPayload(nuevoCli.id));
      this._ops = [nueva, ...this._ops];
      this._updateCount();
      this._renderKanban();
      this._renderLista();
      this._closeModal();
      this._openFicha(nuevoCli.id, nueva.id);
    } catch (err) {
      errorMessageComponent.addError("Error al crear cliente/oportunidad", (err as Error).message);
    } finally {
      btn.disabled = false;
    }
  }

  private _buildOpPayload(clienteId: string): OportunidadSavePayload {
    const p = new OportunidadSavePayload();
    p.clienteId    = clienteId;
    p.source       = this._modalSource;
    p.stage        = "new";   // toda op nueva nace como "Lead nuevo"; proposal solo vía cotización
    p.comercialId  = this._resolveComercialId();
    const proxVal = (document.getElementById("pl-modal-prox") as HTMLInputElement)?.value || "";
    p.proxContacto = proxVal ? new Date(proxVal + "T12:00:00").toISOString() : null;
    p.notas        = (document.getElementById("pl-modal-notas") as HTMLTextAreaElement)?.value ?? "";
    p.productos    = this._readModalProductos();
    return p;
  }

  /**
   * Resuelve el comercialId del campo del modal: select de promotores → id del promotor
   * seleccionado (se envía como comercialId), o null si "Sin asignar".
   */
  private _resolveComercialId(): string | null {
    const el = document.getElementById("pl-modal-comercial") as HTMLSelectElement | null;
    return el?.value || null;
  }

  private _readModalProductos(): { dataag: boolean; rastro: number; bascula: number; vista: boolean; protec: { activo: boolean; cat: string; ha: number } } {
    const isActive = (id: string) => document.getElementById(`pl-mprod-${id}`)?.classList.contains("active") ?? false;
    const val = (id: string, campo: string) => (document.getElementById(`pl-mprod-${id}-${campo}`) as HTMLInputElement | HTMLSelectElement)?.value ?? "";
    const num = (id: string, campo: string) => parseInt(val(id, campo)) || 0;

    return {
      dataag:  isActive("dataag"),
      rastro:  isActive("rastro")  ? num("rastro", "unidades") || 1 : 0,
      bascula: isActive("bascula") ? num("bascula", "unidades") || 1 : 0,
      vista:   isActive("vista"),
      protec:  {
        activo: isActive("protec"),
        cat:    isActive("protec") ? val("protec", "categoria") : "",
        ha:     isActive("protec") ? num("protec", "hectareas") : 0,
      },
    };
  }

  // ── Acciones sobre oportunidades ──────────────────────────────────────────

  private async _changeStage(id: string): Promise<void> {
    const sel = document.getElementById("pl-stage-sel") as HTMLSelectElement | null;
    if (!sel) return;
    const btn = document.getElementById("pl-stage-btn") as HTMLButtonElement;
    btn.disabled = true;
    try {
      const payload = new OportunidadStagePayload();
      payload.stage = sel.value;
      const updated = await PipelineService.instance.changeStage(id, payload);
      this._replaceOp(updated);
      this._renderFichaContent();
    } catch (err) {
      errorMessageComponent.addError("Error al cambiar etapa", (err as Error).message);
      btn.disabled = false;
    }
  }

  private async _addNota(id: string, texto: string): Promise<void> {
    const btn = document.getElementById("pl-note-btn") as HTMLButtonElement | null;
    if (btn) btn.disabled = true;
    try {
      const payload = new EventoPayload();
      payload.tipo = "note";
      payload.metadata = { texto };
      const updated = await PipelineService.instance.addEvento(id, payload);
      this._replaceOp(updated);
      this._renderFichaContent();
    } catch (err) {
      errorMessageComponent.addError("Error al agregar nota", (err as Error).message);
      if (btn) btn.disabled = false;
    }
  }

  private async _deleteOportunidad(id: string): Promise<void> {
    const op = this._ops.find(o => o.id === id);
    if (!op) return;
    if (!window.confirm(`¿Eliminar la oportunidad de "${op.clienteNombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await PipelineService.instance.deleteOportunidad(id);
      this._ops = this._ops.filter(o => o.id !== id);
      this._clienteOps = this._clienteOps.filter(o => o.id !== id);
      if (this._currentOpId === id) this._currentOpId = null;
      this._updateCount();
      this._renderKanban();
      this._renderLista();
    } catch (err) {
      errorMessageComponent.addError("Error al eliminar oportunidad", (err as Error).message);
    }
  }

  /** Actualiza una oportunidad en las listas en memoria. */
  private _replaceOp(updated: OportunidadResponse): void {
    this._ops        = this._ops.map(o => o.id === updated.id ? updated : o);
    this._clienteOps = this._clienteOps.map(o => o.id === updated.id ? updated : o);
    this._updateCount();
    this._renderKanban();
    this._renderLista();
  }

  // ── Motor de precios ──────────────────────────────────────────────────────

  private _calcDataAGForOp(o: OportunidadResponse): number | "negociacion" | null {
    const cli = this._cliente(o.clienteId);
    // El listado de clientes no trae el desglose por finca: se trata como agricultura.
    return this._calcDataAGFromHa(cli?.hectareasTotales ?? 0, false);
  }

  private _calcDataAGFromFincas(fincas: CotizFinca[]): number | "negociacion" | null {
    if (!fincas.length) return null;
    const totalHa = fincas.reduce((s, f) => s + (f.ha || 0), 0);
    const haGan   = fincas.filter(f => f.tipo === "ganadería" || f.tipo === "ganaderia").reduce((s, f) => s + f.ha, 0);
    return this._calcDataAGFromHa(totalHa, haGan > totalHa / 2);
  }

  private _calcDataAGFromHa(totalHa: number, esGan: boolean): number | "negociacion" | null {
    if (totalHa <= 0) return null;
    const precios = PipelineService.instance.precios;

    if (precios) {
      const tabla = (esGan ? precios.ganaderia : precios.agricultura)
        .filter(t => !t.isEliminado)
        .sort((a, b) => a.orden - b.orden);
      for (const tier of tabla) {
        if (tier.isNegociacion) return "negociacion";
        if (tier.topeMaximoHa === null || totalHa <= tier.topeMaximoHa) return tier.precioAnual ?? "negociacion";
      }
      return "negociacion";
    }

    // Fallback hardcodeado (idéntico al mockup)
    if (esGan) {
      if (totalHa <= 200)  return 2_200_000;
      if (totalHa <= 500)  return 2_500_000;
      if (totalHa <= 1000) return 2_750_000;
      if (totalHa <= 2000) return 3_000_000;
      if (totalHa <= 3000) return 4_000_000;
      if (totalHa <= 4000) return 5_000_000;
      if (totalHa <= 5000) return 5_500_000;
      return "negociacion";
    } else {
      if (totalHa <= 50)   return 1_700_000;
      if (totalHa <= 100)  return 2_200_000;
      if (totalHa <= 200)  return 2_700_000;
      if (totalHa <= 300)  return 3_200_000;
      if (totalHa <= 500)  return 3_800_000;
      if (totalHa <= 700)  return 4_500_000;
      if (totalHa <= 1000) return 5_200_000;
      return "negociacion";
    }
  }

  /**
   * Total de RASTRO con IVA: todas las unidades al precio del tramo que corresponde a la cantidad (descuento por
   * volumen). Hasta el 2026-10-25, con las tarifas cargadas se tomaba siempre la primera fila (1–10 u) y el descuento
   * solo se aplicaba con los precios de respaldo.
   */
  private _calcRastro(units: number): number {
    if (units <= 0) return 0;
    return units * this._precioUnitarioRastro(units) * 1.19;
  }

  /** Precio por unidad de RASTRO, sin IVA, para una cantidad: el del tramo que la contiene. */
  private _precioUnitarioRastro(units: number): number {
    const tramos = this._tramosRastro();
    const tramo = tramos.find(t => units >= t.desde && (t.hasta === null || units <= t.hasta))
      ?? [...tramos].reverse().find(t => units >= t.desde)
      ?? tramos[0];
    return tramo.precio;
  }

  /**
   * Tramos de precio de RASTRO, de menor a mayor cantidad. Salen de las filas de «Equipos y servicios adicionales» del
   * menú Costos cuyo nombre dice RASTRO; el rango se lee del nombre ("RASTRO (1–10 u)", "RASTRO (21+ u)") porque la
   * tarifa no tiene campos de rango. Una fila sin rango en el nombre vale para cualquier cantidad. Sin tarifas cargadas,
   * los precios de respaldo del código.
   */
  private _tramosRastro(): readonly TramoRastro[] {
    const filas = (PipelineService.instance.precios?.equipos ?? [])
      .filter(e => !e.isEliminado && /rastro/i.test(e.nombre));
    if (filas.length === 0) return TRAMOS_RASTRO_RESPALDO;
    return filas
      .map((e): TramoRastro => {
        const rango = /(\d+)\s*[–-]\s*(\d+)/.exec(e.nombre);
        if (rango) return { desde: Number(rango[1]), hasta: Number(rango[2]), precio: e.precio };
        const desde = /(\d+)\s*\+/.exec(e.nombre);
        if (desde) return { desde: Number(desde[1]), hasta: null, precio: e.precio };
        return { desde: 1, hasta: null, precio: e.precio };
      })
      .sort((a, b) => a.desde - b.desde);
  }

  /** Total de la Báscula con IVA. */
  private _calcBascula(units: number): number {
    if (units <= 0) return 0;
    return units * this._precioBascula() * 1.19;
  }

  /**
   * Precio por unidad de la Báscula, sin IVA: la fila de «Equipos y servicios adicionales» cuyo nombre dice Báscula o
   * TARA (así se llama la sembrada; hasta el 2026-10-25 solo se buscaba «Báscula» y se usaba siempre el respaldo). Sin
   * tarifas cargadas, el precio de respaldo del código.
   */
  private _precioBascula(): number {
    const fila = PipelineService.instance.precios?.equipos
      .find(e => !e.isEliminado && /b[aá]scula|\btara\b/i.test(e.nombre));
    return fila ? fila.precio : PRECIO_BASCULA_RESPALDO;
  }

  /**
   * Precio de una visita de VISTA: tarifa base + transporte (por cada 25 km de ida y vuelta) + vuelo por hectárea según
   * el tipo + conteo de árboles por hectárea si se pide. Las variables salen del menú Costos y se reconocen por su
   * nombre ahí («Tarifa base», «Costo transporte», «Vuelo agricultura», «Vuelo ganadería», «Conteo de árboles»), que la
   * web no deja cambiar; sin tarifas cargadas, los valores del código. Hasta el 2026-10-25 se buscaban claves que no
   * existen (`base`, `costo_km`, `px_ha_*`) y se usaban siempre los valores del código.
   */
  private _calcVISTA(km: number, ha: number, tipoVuelo: string, arboles: string): VISTAResult | null {
    if (!ha || ha <= 0) return null;
    let base = 1_000_000, costoKm = 90_000, pxHaAgri = 8_000, pxHaGan = 6_000, pxHaConteo = 4_000;
    const precios = PipelineService.instance.precios;
    if (precios) {
      // Sin tildes ni mayúsculas, para que «Vuelo ganadería» y «Vuelo ganaderia» cuenten igual.
      const nombre = (s: string): string => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
      for (const v of precios.vista.filter(v => !v.isEliminado)) {
        switch (nombre(v.variable)) {
          case "tarifa base":       base       = v.valor; break;
          case "costo transporte":  costoKm    = v.valor; break;
          case "vuelo agricultura": pxHaAgri   = v.valor; break;
          case "vuelo ganaderia":   pxHaGan    = v.valor; break;
          case "conteo de arboles": pxHaConteo = v.valor; break;
        }
      }
    }
    const transporte = (km * 2 / 25) * costoKm;
    const pxHa       = (tipoVuelo === "ganadería" || tipoVuelo === "ganaderia") ? pxHaGan : pxHaAgri;
    const vuelo      = ha * pxHa;
    const conteo     = arboles === "si" ? ha * pxHaConteo : 0;
    return { base, transporte, vuelo, conteo, total: base + transporte + vuelo + conteo };
  }

  private _calcPROTEC(catId: string, ha: number): PROTECResult | null {
    if (!catId || ha < 10) return null;
    const precios = PipelineService.instance.precios;
    if (precios) {
      const cat = precios.protec.find(p => p.id === catId && !p.isEliminado);
      if (cat) return { label: cat.categoria, pxHa: cat.precioPorHa, ha, total: cat.precioPorHa * ha };
    }
    const fb = FALLBACK_PROTEC[catId];
    if (!fb) return null;
    return { label: fb.label, pxHa: fb.precio, ha, total: fb.precio * ha };
  }

  private _buildProtecOptions(): string {
    const precios = PipelineService.instance.precios;
    if (precios?.protec?.length) {
      return precios.protec
        .filter(p => !p.isEliminado)
        .sort((a, b) => a.orden - b.orden)
        .map(p => `<option value="${p.id}">${this._esc(p.categoria)}</option>`)
        .join("");
    }
    return Object.entries(FALLBACK_PROTEC)
      .map(([k, v]) => `<option value="${k}">${v.label}</option>`)
      .join("");
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  /**
   * Opciones de promotor. `emptyLabel` distingue el uso: en los formularios la opción vacía
   * significa "Sin asignar" (no hay comercial), en un filtro significa "sin filtrar".
   */
  private _promotorOptions(selectedId: string | null, emptyLabel = "Sin asignar"): string {
    return `<option value="">${emptyLabel}</option>` +
      this._promotores.map(p => `<option value="${p.id}" ${p.id === selectedId ? "selected" : ""}>${this._esc(p.nombre)}</option>`).join("");
  }

  /**
   * Bloque "Productos cotizados" de la ficha. `ha`/`esGan` vienen de `_fichaHa()` (fincas reales),
   * no del cliente cacheado.
   *
   * Un producto seleccionado SIEMPRE se lista: si no se puede tarifar se muestra el motivo en vez
   * del monto. Antes se omitía la línea entera, así que DATA AG y VISTA (los dos que dependen de
   * hectáreas) desaparecían de la ficha cuando el cliente aún no tenía fincas sincronizadas y
   * parecía que la oportunidad se había guardado sin ellos.
   *
   * `hideTotal` (op ya cotizada) suprime el total estimado: el número que vale es el `valorCierre`.
   */
  private _buildProductosPricingHtml(p: OportunidadProductos | null, ha: number, esGan: boolean, hideTotal = false): string {
    if (!p) return `<div class="pl-ficha-field"><span>Productos</span><span>—</span></div>`;
    p = this._productos.filtrar(p);   // sin lo que la marca no vende

    const daLabel   = getServicio("dataag")?.nombre ?? "DATA AG";
    const rastroLabel = getServicio("rastro")?.nombre ?? "RASTRO GPS";
    const bascLabel   = getServicio("bascula")?.nombre ?? "Báscula";
    const vistaLabel  = getServicio("vista")?.nombre ?? "VISTA";
    const protecLabel = getServicio("protec")?.nombre ?? "PROTEC";

    const lines: string[] = [];
    let total = 0;

    // Fila del desglose. `muted` = no hay precio todavía (falta un dato), se explica en vez de omitir.
    let pendientes = 0;
    const row = (desc: string, right: string, muted = false): string => {
      if (muted) pendientes++;
      return `<div style="display:flex;justify-content:space-between;font-size:13px;padding:4px 0${muted ? ";color:var(--sx-text-tertiary)" : ""}"><span>${desc}</span><span${muted ? "" : ` style="font-family:var(--sx-font-mono);font-weight:600"`}>${right}</span></div>`;
    };

    const SIN_HA = "Sin hectáreas registradas";

    if (p.dataag) {
      const v = this._calcDataAGFromHa(ha, esGan);
      if (typeof v === "number") { total += v; lines.push(row(`${daLabel} — Suscripción anual`, `$${Math.round(v).toLocaleString("es-CO")}/año`)); }
      else if (v === "negociacion") lines.push(`<div style="display:flex;justify-content:space-between;font-size:13px;padding:4px 0;color:var(--sx-warning-text)"><span>${daLabel}</span><span>Negociación directa</span></div>`);
      else lines.push(row(`${daLabel} — Suscripción anual`, SIN_HA, true));
    }
    if (p.rastro > 0) {
      const v = this._calcRastro(p.rastro); total += v;
      const unitPrice = p.rastro <= 10 ? 530000 : p.rastro <= 20 ? 500000 : 470000;
      lines.push(row(`${rastroLabel} ×${p.rastro} (${unitPrice.toLocaleString("es-CO")}/u +IVA)`, `$${Math.round(v).toLocaleString("es-CO")}`));
    }
    if (p.bascula > 0) {
      const v = this._calcBascula(p.bascula); total += v;
      lines.push(row(`${bascLabel} ×${p.bascula} (750.000/u +IVA)`, `$${Math.round(v).toLocaleString("es-CO")}`));
    }
    if (p.vista) {
      const tv = esGan ? "ganadería" : "agricultura";
      const vr = this._calcVISTA(0, ha, tv, "no");
      if (vr) { total += vr.total; lines.push(row(`${vistaLabel} — Fotogrametría`, `$${Math.round(vr.total).toLocaleString("es-CO")}/visita`)); }
      else lines.push(row(`${vistaLabel} — Fotogrametría`, SIN_HA, true));
    }
    if (p.protec?.activo) {
      const pr = p.protec.cat ? this._calcPROTEC(p.protec.cat, p.protec.ha) : null;
      if (pr) { total += pr.total; lines.push(row(`${protecLabel} — ${pr.label} (${pr.ha} ha × $${pr.pxHa.toLocaleString("es-CO")}/ha)`, `$${pr.total.toLocaleString("es-CO")}/aplicación`)); }
      else lines.push(row(`${protecLabel} — Aspersión`, p.protec.cat ? "Mínimo 10 ha requerido" : "Falta categoría de cultivo", true));
    }

    if (lines.length === 0) return `<div class="pl-ficha-field"><span>Productos</span><span>${this._esc(this._buildProductoStr(p))}</span></div>`;

    // Con cotización generada manda el `valorCierre` pactado: se omite el total estimado para que
    // no compita con él. Los precios por línea se quedan (son el desglose), pero siguen siendo
    // cálculo en vivo y pueden no cuadrar con el PDF — sobre todo VISTA, cuyo km no se persiste.
    const totalHtml = hideTotal
      ? `<div class="pl-hint" style="margin-top:6px">Precios de referencia · el valor pactado es el cotizado.</div>`
      : `<hr class="pl-divider" style="margin:6px 0">
      <div style="display:flex;justify-content:space-between;font-size:14px;font-weight:700;padding:4px 0"><span>Total estimado anual${pendientes > 0 ? " (parcial)" : ""}</span><span style="font-family:var(--sx-font-mono);color:var(--sx-success-text)">$${Math.round(total).toLocaleString("es-CO")} COP</span></div>
      ${pendientes > 0 ? `<div class="pl-hint" style="margin-top:4px">${pendientes} producto${pendientes === 1 ? "" : "s"} sin tarifar — el total no los incluye.</div>` : ""}`;

    return `<div class="pl-prods-section">
      <div class="pl-ficha-sec" style="margin-bottom:8px">Productos cotizados</div>
      ${lines.join("")}
      ${totalHtml}
    </div>`;
  }

  private _buildProductoStr(p: OportunidadProductos | null): string {
    if (!p) return "—";
    p = this._productos.filtrar(p);   // sin lo que la marca no vende
    const daLabel   = getServicio("dataag")?.nombre ?? "DATA AG";
    const rastroLabel = getServicio("rastro")?.nombre ?? "RASTRO";
    const bascLabel   = getServicio("bascula")?.nombre ?? "Báscula";
    const vistaLabel  = getServicio("vista")?.nombre ?? "VISTA";
    const protecLabel = getServicio("protec")?.nombre ?? "PROTEC";
    const parts: string[] = [];
    if (p.dataag)         parts.push(daLabel);
    if (p.rastro > 0)     parts.push(`${rastroLabel} ×${p.rastro}`);
    if (p.bascula > 0)    parts.push(`${bascLabel} ×${p.bascula}`);
    if (p.vista)          parts.push(vistaLabel);
    if (p.protec?.activo) parts.push(protecLabel);
    return parts.join(" + ") || "—";
  }

  /**
   * Convierte un valor de fecha a unix ms. Acepta: number (unix ms), string de dígitos
   * (int64 serializado como string por el backend), o ISO string. Devuelve NaN si no es válido.
   */
  private _toMs(v: number | string | null | undefined): number {
    if (v === null || v === undefined || v === "") return NaN;
    if (typeof v === "number") return v;
    const str = v.trim();
    if (/^-?\d+$/.test(str)) return Number(str);   // int64 (unix ms) serializado como string
    return new Date(str).getTime();                // ISO string
  }

  /** Formatea un timestamp (unix ms, string numérica, ISO, o inválido/ausente) a fecha corta es-CO. */
  private _fmtTs(v: number | string | null | undefined): string {
    const t = this._toMs(v);
    return isNaN(t) ? "—" : new Date(t).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
  }

  private _fmtDate(iso: string | null): string {
    if (!iso) return "—";
    const d = new Date(typeof iso === "string" && !iso.includes("T") ? iso + "T00:00:00" : iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
  }

  private _daysAgo(ts: number | string | null | undefined): string {
    const t = this._toMs(ts);
    if (isNaN(t)) return "";
    const d = Math.round((Date.now() - t) / 86_400_000);
    if (d === 0) return "Hoy";
    if (d === 1) return "Hace 1 día";
    return `Hace ${d} días`;
  }

  private _daysUntil(iso: string | null): string {
    if (!iso) return "";
    const d = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
    if (d < 0) return `Vencido hace ${Math.abs(d)} día${Math.abs(d) !== 1 ? "s" : ""}`;
    if (d === 0) return "Hoy";
    if (d === 1) return "Mañana";
    return `En ${d} días`;
  }

  private _isOverdue(iso: string | null): boolean {
    return !!iso && new Date(iso) < new Date();
  }

  private _defaultCotizProds(): CotizProds {
    return { dataag: true, rastro: 0, bascula: 0, vista: false, protec: { activo: false, cat: "", ha: 0 } };
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
